import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import './App.css';
const FONTS = {
  display: "'Noto Serif', 'Georgia', serif",
  body: "'Noto Sans', sans-serif",
  mono: "'Courier New', monospace",
};

const RENGAY_STRUCTURE = [
  { lines: 3, label: "Verse 1", hint: "3 lines · haiku", author: "A" },
  { lines: 2, label: "Verse 2", hint: "2 lines · couplet", author: "B" },
  { lines: 3, label: "Verse 3", hint: "3 lines · haiku", author: "A" },
  { lines: 3, label: "Verse 4", hint: "3 lines · haiku", author: "B" },
  { lines: 2, label: "Verse 5", hint: "2 lines · couplet", author: "A" },
  { lines: 3, label: "Verse 6", hint: "3 lines · haiku", author: "B" },
];

const formatDate = (ts) => new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });


const enforceLineCount = (value, maxLines) => {
  const lines = value.split("\n");
  if (lines.length > maxLines) return lines.slice(0, maxLines).join("\n");
  return value;
};

function PasswordInput({ value, onChange, placeholder, style = {} }) {
  const [show, setShow] = React.useState(false);
  return (
    <div style={{position: "relative", width: "100%"}}>
      <input
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={{width: "100%", paddingRight: 38, boxSizing: "border-box", ...style}}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        style={{position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#666", fontSize: "1rem", lineHeight: 1, padding: 2}}
        tabIndex={-1}
      >{show ? "🙈" : "👁"}</button>
    </div>
  );
}

function InfoTip({ text }) {
  const [show, setShow] = React.useState(false);
  return (
    <span style={{position: "relative", display: "inline-flex", alignItems: "center", marginLeft: 4}}>
      <span
        onClick={e => { e.stopPropagation(); setShow(s => !s); }}
        style={{cursor: "pointer", color: "var(--accent)", fontSize: "0.6rem", border: "1px solid var(--accent)", borderRadius: "50%", width: 13, height: 13, display: "inline-flex", alignItems: "center", justifyContent: "center", opacity: 0.7, flexShrink: 0}}
      >?</span>
      {show && (
        <>
          <span onClick={() => setShow(false)} style={{position: "fixed", inset: 0, zIndex: 9}} />
          <span style={{position: "absolute", left: 18, top: -6, background: "#2a2a2a", border: "1px solid rgba(255,255,255,0.12)", color: "#c8bfb0", padding: "8px 10px", borderRadius: 7, fontSize: "0.7rem", width: 195, zIndex: 10, lineHeight: 1.55, boxShadow: "0 4px 16px rgba(0,0,0,0.5)"}}>
            {text}
          </span>
        </>
      )}
    </span>
  );
}


// ── Info Pages ─────────────────────────────────────────────────
function InfoPage({ page, onClose }) {
  return (
    <div className="info-overlay">
      <div className="info-panel">
        <div className="info-header">
          <button className="back-nav-btn" onClick={onClose}>← Back</button>
        </div>
        {page === "what" && (
          <div className="info-content">
            <h1 className="info-title">What is Rengay?</h1>
            <div className="info-body">
              <p>Rengay is a collaborative six-verse poem written by two poets, alternating three-line and two-line verses in a fixed pattern. Unlike older forms of linked verse, all six verses share a single unifying theme — an image, moment, or idea that holds the whole poem together.</p>
              <h2>Where it comes from</h2>
              <p>Rengay was invented in August 1992 by California poet Garry Gay, who created it as a more accessible alternative to the ancient Japanese forms of renga and renku. Gay felt that the complex rules of those traditions were often too culturally specific to resonate with North American poets. He wanted a shorter, more intimate form — one that stayed close to the spirit of haiku while allowing two voices to explore a theme in depth.</p>
              <p>The name rengay is a combination of "renga" (the Japanese tradition of linked verse) and Gay's own last name. He and poet Michael Dylan Welch wrote the very first rengay together — "Deep Winter" — on August 9, 1992, in Foster City, California.</p>
              <h2>How it works</h2>
              <p>A two-person rengay follows a precise pattern of six verses. Poet A writes Verses 1, 3, and 5; Poet B writes Verses 2, 4, and 6. The verse lengths alternate as follows:</p>
              <ul>
                <li>Verse 1 — Poet A — 3 lines (haiku)</li>
                <li>Verse 2 — Poet B — 2 lines (couplet)</li>
                <li>Verse 3 — Poet A — 3 lines (haiku)</li>
                <li>Verse 4 — Poet B — 3 lines (haiku)</li>
                <li>Verse 5 — Poet A — 2 lines (couplet)</li>
                <li>Verse 6 — Poet B — 3 lines (haiku)</li>
              </ul>
              <p>This arrangement — A(3), B(2), A(3), B(3), A(2), B(3) — gives each poet the same number of lines overall and creates a natural balance between the two voices. The first poet leads in the opening, and the second poet closes the poem.</p>
              <h2>Theme and linking</h2>
              <p>The single most important feature of a rengay is its unifying theme. This is what distinguishes it from renku, which deliberately avoids thematic development. In a rengay, every verse should connect to the shared theme — though each verse approaches it from a different angle. Verses link to one another through contrast, association, intuition, wit, even puns at times. This technique of "linking and shifting" keeps the poem moving forward while maintaining coherence. It's an art that is perfected with every rengay you craft!</p>
              <h2>The English-language tradition</h2>
              <p>Rengay is deeply rooted in the English-language haiku (ELH) tradition. It draws on Western poetic sensibilities — directness, imagism, concrete language — rather than strict adherence to Japanese conventions like seasonal references or syllable counts. Many ELH poets prefer to write by rhythm and breath rather than by counting syllables, and rengay welcomes this approach.</p>
              <p>Since its invention, rengay has been written in many languages around the world. The Haiku Poets of Northern California has held an annual rengay contest since 1995, and the Haiku Society of America added rengay to its annual contests in 2020. The first dedicated rengay journal, <em>Tandem</em>, began publication in 2021.</p>
            </div>
          </div>
        )}
        {page === "how" && (
          <div className="info-content">
            <h1 className="info-title">How to Write Rengay</h1>
            <div className="info-body">
              <p>Writing a rengay is a conversation and a dance between two poets. It requires the skills of haiku — precision, imagery, a feel for the moment — plus the additional art of responding to another voice. Here is a practical guide to getting started.</p>

              <h2>Before you begin</h2>
              <p>A good rengay starts with a good primary theme, rich enough to sustain six verses and able to unify the entire work. Secondary themes can be created as well. The theme can be something concrete (like a day at an amusement park, or items in a refrigerator), or something abstract (like hope, or justice). Sometimes themes may change as a work unfolds and that's fine. There may also be secondary themes poets can work with. It's all in how you approach it!</p>
              <p>It also helps to have some experience writing haiku before attempting rengay. Each verse in a rengay — including the two-line couplets — should have haiku quality with enough space for the reader to enter.</p>
              <p>Working with a partner can be both challenging and deeply rewarding. It is common — and encouraged — for poets to help shape each other's verses, whether to better develop the shared theme or to strengthen an individual verse. This kind of active collaboration makes a rengay more than just an assemblage of themed haiku; it becomes something neither poet could have written alone.</p>
              <p>This means diplomacy matters. If a verse isn't quite landing, resist the urge to rewrite it yourself — that pulls the poem back toward solo work. Instead, share your honest response and using positive language ask questions to develop the verse in question. A good rengay partner is just that: a partner, and partners are willing to work together until you both feel you've got it right. A good partnership starts with YOU being a good partner. The Poet's Chat is a good place for this kind of conversation.</p>

              <h2>Opening the poem — Verse 1</h2>
              <p>The first verse is the most important. It sets the tone, establishes the theme, and invites the second poet in. Write it as you would write a haiku: an image, a moment in time, something observed rather than explained.</p>
              <p>In this app, you write Verse 1 alone and post it to the "Join a Rengay" area where a second poet will join you. Your opening verse is an invitation — write it with that spirit in mind.</p>

              <h2>Responding — linking and shifting</h2>
              <p>When you respond to another poet's verse, your job is to both connect with what came before and move the poem forward. This is the art of "linking and shifting." You can link by picking up an image from the previous verse and turning it in a new direction. You can shift by changing the scene, the season, the scale, or the mood — while staying true to the shared theme.</p>
              <p>A response that merely restates the previous verse is too tight. A response with no connection at all is too loose. Aim for something in between: a new angle on the same subject, a surprising but recognizable turn.</p>

              <h2>The two-line verses</h2>
              <p>The two-line couplets (Verses 2 and 5) are often the most challenging to write. They should not be three-line haiku squeezed into two lines. Instead, think of them as complete haiku in their own right — just shorter, more compressed. They are often the pivots of the poem, the moments where the theme shifts most sharply. Give them the same care and attention as the three-line verses.</p>

              <h2>The closing verse</h2>
              <p>Verse 6 closes the poem. It should feel like a resolution — not a tidy conclusion, but a sense of arrival, of the poem having gone somewhere. Return to the theme with fresh eyes. Closing verses can sometimes echo the opening in some way, creating a feeling of completeness without being too obvious about it. Experiment, have fun!</p>

              <h2>A few practical tips</h2>
              <ul>
                <li>Each verse should be strong enough to stand on its own — but it should also work to develop the poem as a whole. The best verses do both.</li>
                <li>Write in the present tense where possible. Haiku lives in the immediate moment.</li>
                <li>Use concrete, specific language. Not "a tree" but "a bare oak." Not "sadness" but "an empty chair."</li>
                <li>In English-language haiku, syllable counts are a guide, not a rule. Write by ear and breath rather than by rigid counting.</li>
                <li>Don't rush. Rengay is meant to unfold over time. Let each verse settle before responding.</li>
                <li>Read the poem as a whole as it develops. Use the "View Rengay" button to see how the verses are working together.</li>
              </ul>

              <h2>Further reading</h2>
              <ul>
                <li><a href="https://www.rengay.com/rengay-essays/essays-on-rengay-by-others/rengay-the-art-of-partnering" target="_blank" rel="noreferrer">Rengay: The Art of Partnering</a></li>
                <li><a href="https://www.rengay.com/rengay-essays/essays-on-rengay-by-others/collaboration-exploring-rengay" target="_blank" rel="noreferrer">Collaboration: Exploring Rengay</a></li>
                <li><a href="https://haikupedia.org/article-haikupedia/rengay/" target="_blank" rel="noreferrer">Haikupedia: Rengay</a> — a thorough overview of the form and its history</li>
                <li><a href="https://www.hsa-haiku.org/rengayawards/rengay.htm" target="_blank" rel="noreferrer">HSA Rengay Awards</a> — the Haiku Society of America's annual rengay contest and resources</li>
                <li><a href="http://www.baymoon.com/~ariadne/form/rengay.htm" target="_blank" rel="noreferrer">Poetry Form: The Rengay</a> — J. Zimmerman's detailed structural guide</li>
              </ul>
            </div>
          </div>
        )}
        {page === "howto" && (
          <div className="info-content">
            <h1 className="info-title">How to Use This App</h1>
            <div className="info-body">

              <h2>Home</h2>
              <p>The home page gives you an overview of the app and quick access to all four main areas — Start a Rengay, Join a Rengay, Rengay in Progress, and Library. From the navigation you can also access My Rengay, which shows only the poems you're personally involved in as a poet. If Ba Poetry has sent you a message, a button will appear on the home screen to open the conversation.</p>

              <h2>Start a Rengay</h2>
              <p>This is where you begin a new poem as Poet A. Give your rengay a title and a unifying theme — both can be changed later from the Manage section of the poem. Then write Verse 1, a three-line haiku that opens the poem. When you're happy with it, click <strong>"Verse 1 Complete"</strong> and then <strong>"Post to Join a Rengay."</strong> Your opening verse will appear in the Join a Rengay area for another poet to discover. If you change your mind before anyone joins, you can withdraw your rengay using the <strong>Withdraw</strong> button on its card.</p>

              <h2>Join a Rengay</h2>
              <p>This is where you'll find rengay waiting for a second poet. Browse the list, click any card to expand it and read Verse 1, and if you'd like to respond, write Verse 2 — a two-line couplet. Both lines must have content before the <strong>"Verse 2 Complete"</strong> button becomes available. Once marked complete, click <strong>"Join this Rengay as Poet B."</strong> The rengay immediately moves to Rengay in Progress and is now closed to just the two of you. You can also leave a suggestion or comment on any rengay here using the 💬 button, even if you don't join it.</p>

              <h2>Rengay in Progress</h2>
              <p>This is where the two poets work together to complete the remaining verses. Click a card to expand it. Only the verse assigned to you is open for writing, and only when it is next in sequence — verses must be written in order. When you've written a verse, click <strong>"Verse X Complete"</strong> to lock it in. If you need to revisit a completed verse, click <strong>"Edit Your Verse"</strong> to reopen it. You can also chat with your writing partner in the Poet's Chat area at the bottom of each poem. This chat area can only be seen by the two poets, and is a wonderful place to work together to build the poem.</p>
              <p>Click <strong>"View Rengay"</strong> at any time to see a clean reading view of the completed verses so far. When all six verses are complete, either poet can click <strong>"Mark as Complete — Move to Library."</strong> Anyone in the community can read rengay in progress and leave suggestions using the 💬 button.</p>

              <h2>Managing a rengay in progress</h2>
              <p>At the bottom of the poems you are writing you'll find a <strong>"Manage this rengay"</strong> section with several options. You can edit the title or theme at any time. If you need to leave a poem, you can step out of your own role — the rengay will be tagged <strong>"Seeking New Poet"</strong> and remain open until another community member steps in. If both poets agree to abandon a rengay entirely, either poet can request abandonment. The poet who made the request will see a message that they're waiting for their partner to confirm. Their partner poet will see the option to confirm or decline. One poet alone cannot permanently delete a shared rengay.</p>

              <h2>Notifications</h2>
              <p>You can opt in to email notifications to stay connected with your poems. Available alerts include: when your co-poet completes a verse, when a poem is finished and moves to the Library, when a new rengay appears in Join a Rengay, and a gentle nudge if a rengay you're working on has had no activity for seven days. Your notification preferences can be managed in the <strong>My Rengay</strong> section under <strong>Email Notification Preferences</strong>.</p>

              <h2>My Rengay</h2>
              <p>My Rengay is your personal view of the app, showing only the poems you're actively involved in as a poet — poems awaiting a partner, poems in progress, and your completed works. It shows which verse is currently yours to write, and when you're waiting for your partner to respond. At the top of My Rengay you'll find your <strong>Email Notification Preferences</strong>, where you can toggle each type of alert on or off. At the bottom you'll find the option to <strong>delete your account</strong> if you wish to leave the community.</p>

              <h2>Library</h2>
              <p>The Library is where completed rengay live, readable by all community members. Click any card or the <strong>"View Rengay"</strong> button to open a clean reading view of the poem. The poets' names appear at the bottom of the poem. If you are one of the poets who crafted the rengay, you can leave a comment using the 💬 <strong>Comments</strong> button, a shared space where the two poets can add context, allusions, or notes about the poem. This information is visible to all readers once added. If both poets agree to withdraw a poem from the Library, either poet can request withdrawal from within the reading view — the other poet must confirm before it is removed.</p>

              <h2>Suggestions & Comments</h2>
              <p>The 💬 button appears on rengay throughout the app. On poems in the Join and Rengay in Progress areas, you can suggest a specific verse or leave a general comment. On completed poems in the Library, you can leave general comments only. All suggestions and comments are visible to everyone in the community. You can delete your own suggestions and comments at any time.</p>

            </div>
          </div>
        )}
        {page === "copyright" && (
          <div className="info-content">
            <h1 className="info-title">Terms of Service</h1>
            <div className="info-body">
              <h2>Community Standards</h2>
              <p>場 Ba is a space for poetry, creativity, and collaboration. All users are expected to treat one another with kindness and respect. Racism, sexism, harassment, cruelty, and hate speech of any kind are not tolerated and will result in removal from the platform.</p>
              <h2>The App</h2>
              <p>This application — including its design, code, structure, and all original content — is the creation of <strong>AG Studios</strong>. All rights reserved. Unauthorized reproduction, distribution, or modification of this application or its components is prohibited.</p>
              <p>© {new Date().getFullYear()} AG Studios. All rights reserved.</p>
              <h2>The Poems</h2>
              <p>The rengay and other creative works displayed through this application are the sole intellectual property of their authors. AG Studios makes no claim of ownership over any poem, verse, or creative work submitted by users of this app.</p>
              <p>The rights to any collaborative work — including rengay written by two poets together — belong to the authors themselves. Any agreement between co-authors regarding the ownership, publication, or reproduction of their collaborative work is a private matter between those authors. AG Studios is not a party to any such agreement and accepts no responsibility for disputes arising from collaborative authorship.</p>
              <h2>Display of Completed Works</h2>
              <p>Completed rengay are displayed in the app's Library, where they are visible to members of the community. AG Studios does not consider the display of works within this app to constitute "publication" in any formal literary or legal sense. Authors who wish to submit their work for publication elsewhere retain full rights to do so, and the display of a poem in this app's Library should not be considered prior publication for the purposes of any submission guidelines. Ba Poetry is best understood as a workshop space — a place where poets develop and refine their collaborative work, not a venue of record for final, published poems.</p>
              <h2>User Responsibility</h2>
              <p>By participating in this community, users confirm that the work they submit is their own original writing and does not infringe upon the copyright of any third party. AG Studios reserves the right to remove any creative work from the app, and to remove any member from the community, at its sole discretion and for any reason, without prior notice.</p>
              <h2>Contact & Report a Violation</h2>
              <p>For questions, concerns, or to report a violation of these terms, please <a href="mailto:haikutheory@gmail.com">contact us directly</a>.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Clean Reading View ─────────────────────────────────────────
function CleanReadingView({ rengay, onBack, isLibrary, username, onOpenSuggestions, onRequestWithdrawal, onConfirmWithdrawal, onCancelWithdrawal, onSaveInfo }) {
  const [showInfo, setShowInfo] = React.useState(false);
  const [infoText, setInfoText] = React.useState(rengay.info || "");
  const [infoEditing, setInfoEditing] = React.useState(false);

  const completedVerses = isLibrary
    ? RENGAY_STRUCTURE.map((s, i) => ({ ...s, text: rengay.verses[i] })).filter(v => v.text?.trim())
    : RENGAY_STRUCTURE.map((s, i) => ({ ...s, text: rengay.verses[i], complete: rengay.verseComplete?.[i] })).filter(v => v.complete && v.text?.trim());

  const isPoetA = username === rengay.authorA;
  const isPoetB = username === rengay.authorB;
  const isPoet = isPoetA || isPoetB;
  const myRole = isPoetA ? "A" : isPoetB ? "B" : null;
  const withdrawalRequestedBy = rengay.withdrawalRequestedBy || null;
  const hasInfo = (rengay.info || "").trim().length > 0;
  const showInfoButton = isPoet || hasInfo;

  const handleSaveInfo = () => {
    onSaveInfo && onSaveInfo(rengay.id, infoText);
    setInfoEditing(false);
  };

  return (
    <div className="clean-view">
      <h1 className="clean-title">{rengay.title}</h1>
      <div className="clean-divider" />
      <div className="clean-verses">
        {completedVerses.length === 0
          ? <p style={{ color: "var(--muted)", fontStyle: "italic", textAlign: "center" }}>No completed verses yet.</p>
          : completedVerses.map((v, i) => <pre key={i} className="clean-verse" style={v.author === "B" ? {fontStyle: "italic"} : {}}>{v.text}</pre>)
        }
      </div>
      <div className="clean-divider" />

      {isLibrary && (
        <>
          {/* Poet names */}
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <p style={{ fontFamily: "Georgia, serif", color: "var(--muted)", fontSize: "0.88rem", letterSpacing: "0.08em", fontStyle: "italic" }}>
              {rengay.authorA} & {rengay.authorB}
            </p>
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 28, flexWrap: "wrap" }}>
            <button onClick={() => onOpenSuggestions && onOpenSuggestions(rengay.id)}
              style={{ background: "transparent", border: "1px solid var(--border)", borderRadius: 20, color: "var(--muted)", fontSize: "0.8rem", padding: "7px 18px", cursor: "pointer" }}>
              💬 Comments
            </button>
            {showInfoButton && (
              <button onClick={() => setShowInfo(s => !s)}
                style={{ background: showInfo ? "rgba(201,168,76,0.08)" : "transparent", border: `1px solid ${showInfo ? "rgba(201,168,76,0.3)" : "var(--border)"}`, borderRadius: 20, color: showInfo ? "var(--accent)" : "var(--muted)", fontSize: "0.8rem", padding: "7px 18px", cursor: "pointer" }}>
                ℹ Information about this Rengay
              </button>
            )}
          </div>

          {/* Info panel */}
          {showInfo && (
            <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid var(--border)", borderRadius: 10, padding: "20px 22px", marginBottom: 28 }}>
              {isPoet && infoEditing ? (
                <>
                  <textarea value={infoText} onChange={e => setInfoText(e.target.value)}
                    placeholder="Add context, allusions, or notes about this rengay…"
                    rows={5}
                    style={{ width: "100%", background: "transparent", border: "none", color: "var(--text)", fontSize: "0.88rem", lineHeight: 1.8, fontFamily: "Georgia, serif", resize: "vertical", outline: "none" }} />
                  <div style={{ display: "flex", gap: 10, marginTop: 12, justifyContent: "flex-end" }}>
                    <button onClick={() => setInfoEditing(false)} style={{ background: "transparent", border: "1px solid var(--border)", borderRadius: 6, color: "var(--muted)", fontSize: "0.76rem", padding: "5px 14px", cursor: "pointer" }}>Cancel</button>
                    <button onClick={handleSaveInfo} style={{ background: "var(--accent)", border: "none", borderRadius: 6, color: "#1a1a1a", fontSize: "0.76rem", fontWeight: 600, padding: "5px 14px", cursor: "pointer" }}>Save</button>
                  </div>
                </>
              ) : (
                <>
                  {infoText.trim()
                    ? <p style={{ color: "var(--text)", fontSize: "0.88rem", lineHeight: 1.8, fontFamily: "Georgia, serif", whiteSpace: "pre-wrap" }}>{infoText}</p>
                    : <p style={{ color: "var(--muted)", fontSize: "0.85rem", fontStyle: "italic" }}>No information added yet.</p>
                  }
                  {isPoet && (
                    <button onClick={() => setInfoEditing(true)} style={{ marginTop: 12, background: "transparent", border: "1px solid var(--border)", borderRadius: 6, color: "var(--muted)", fontSize: "0.74rem", padding: "4px 12px", cursor: "pointer" }}>
                      {infoText.trim() ? "Edit" : "Add information"}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Withdrawal — other poet confirms */}
          {withdrawalRequestedBy && isPoet && myRole !== withdrawalRequestedBy && (
            <div className="abandon-notice" style={{ marginBottom: 20 }}>
              <p className="abandon-notice-text">
                <strong>{withdrawalRequestedBy === "A" ? rengay.authorA : rengay.authorB}</strong> has requested to withdraw this rengay from the Library. Confirm to remove it.
              </p>
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button className="danger-btn abandon-btn" onClick={() => onConfirmWithdrawal(rengay.id)}>Confirm — Withdraw from Library</button>
                <button className="danger-btn" onClick={() => onCancelWithdrawal(rengay.id)}>Decline</button>
              </div>
            </div>
          )}

          {/* Withdrawal — requester waiting */}
          {withdrawalRequestedBy && isPoet && myRole === withdrawalRequestedBy && (
            <div className="abandon-notice" style={{ marginBottom: 20 }}>
              <p className="abandon-notice-text">You've requested to withdraw this rengay. Waiting for your partner to confirm.</p>
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button className="danger-btn" onClick={() => onCancelWithdrawal(rengay.id)}>Cancel Request</button>
              </div>
            </div>
          )}

          {/* Request withdrawal */}
          {isPoet && !withdrawalRequestedBy && (
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <button onClick={() => onRequestWithdrawal(rengay.id, myRole)}
                style={{ background: "transparent", border: "1px solid #5a2020", borderRadius: 6, color: "#a05050", fontSize: "0.7rem", padding: "5px 14px", cursor: "pointer" }}>
                Request Withdrawal from Library
              </button>
            </div>
          )}
        </>
      )}

      <button className="back-nav-btn" style={{ display: "block", margin: "0 auto" }} onClick={onBack}>
        ← Back to {isLibrary ? "Library" : "Rengay in Progress"}
      </button>
    </div>
  );
}

// ── Suggestions Panel ──────────────────────────────────────────
function SuggestionsPanel({ poemId, suggestions, onAddSuggestion, onDeleteSuggestion, onClose, commentsOnly, username, currentUserId }) {
const [name, setName] = useState(username || "");
  const [verseNum, setVerseNum] = useState("1");
  const [text, setText] = useState("");
  const [type, setType] = useState(commentsOnly ? "comment" : "suggestion");
  const mine = suggestions.filter(s => s.poemId === poemId);

  const handleSubmit = () => {
    if (!text.trim()) return;
    onAddSuggestion({ poemId, name: name || "Anonymous", verseNum: type === "suggestion" ? verseNum : null, text, type });
    setText(""); setName("");
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="side-panel" onClick={e => e.stopPropagation()}>
        <div className="panel-header">
          <span className="panel-title">Suggestions & Comments</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <p className="panel-desc">{commentsOnly ? "Leave a comment on this completed poem. Visible to everyone." : "Leave a suggested verse or comment. Visible to everyone."}</p>
        <div className="suggestion-form">
          <input className="sugg-input" placeholder="Your name…" value={name} readOnly />
          {!commentsOnly && (
            <div className="sugg-type-row">
              <button className={`sugg-type-btn ${type === "suggestion" ? "active" : ""}`} onClick={() => setType("suggestion")}>Suggest a verse</button>
              <button className={`sugg-type-btn ${type === "comment" ? "active" : ""}`} onClick={() => setType("comment")}>General comment</button>
            </div>
          )}
          {!commentsOnly && type === "suggestion" && (
            <select className="sugg-input" value={verseNum} onChange={e => setVerseNum(e.target.value)}>
              {RENGAY_STRUCTURE.map((v, i) => (
                <option key={i} value={String(i + 1)}>Verse {i + 1} ({v.lines === 3 ? "3-line haiku" : "2-line couplet"})</option>
              ))}
            </select>
          )}
          <textarea className="sugg-textarea" placeholder={type === "suggestion" ? "Write your suggested verse…" : "Write your comment…"} value={text} onChange={e => setText(e.target.value)} rows={3} />
          <button className="sugg-submit-btn" onClick={handleSubmit}>{type === "suggestion" ? "Submit Suggestion" : "Post Comment"}</button>
        </div>
        <div className="sugg-list">
          {mine.length === 0
            ? <div className="sugg-empty">No suggestions yet — be the first!</div>
            : mine.map(s => (
              <div key={s.id} className={`sugg-item ${s.type === "comment" ? "sugg-comment" : "sugg-verse"}`}>
                <div className="sugg-item-header">
                  <span className="sugg-author">{s.name}</span>
                  {s.type === "suggestion" && <span className="sugg-verse-tag">Verse {s.verseNum}</span>}
                  {s.type === "comment" && <span className="sugg-comment-tag">Comment</span>}
                  <span className="sugg-date">{formatDate(s.ts)}</span>
                  {s.userId && s.userId === currentUserId && (
                    <button onClick={() => onDeleteSuggestion(s.id)} style={{marginLeft: "auto", background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: "0.75rem", padding: "0 2px", lineHeight: 1}} title="Delete">×</button>
                  )}
                </div>
                <pre className="sugg-text">{s.text}</pre>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}

// ── Start a Rengay ─────────────────────────────────────────────
function StartRengayForm({ onStart, onCancel, username }) {
  const [title, setTitle] = useState("");
  const [theme, setTheme] = useState("");
  const [authorA, setAuthorA] = useState(username || "");
useEffect(() => { if (username) setAuthorA(username); }, [username]);
  const [verse1, setVerse1] = useState("");
  const [verse1Complete, setVerse1Complete] = useState(false);
  const [titleError, setTitleError] = useState(false);

  const handleSubmit = () => {
    if (!verse1.trim() || !verse1Complete) return;
    if (!title.trim()) { setTitleError(true); return; }
    setTitleError(false);
    const verses = RENGAY_STRUCTURE.map(() => "");
    verses[0] = verse1;
    onStart({
      type: "rengay", status: "awaiting",
      title: title.trim(), theme, authorA, authorB: "",
      verses,
      verseComplete: [true, false, false, false, false, false],
      ts: Date.now(),
    });
    setTitle(""); setTheme(""); setAuthorA(""); setVerse1(""); setVerse1Complete(false);
  };

  const linesReady = verse1.split("\n").length === 3 && verse1.trim().length > 0;

  return (
    <div className="editor-card">
      <div className="editor-header">
        <span className="form-badge rengay-badge">Start a Rengay</span>
        <p className="form-desc">You will be Poet A. Write Verse 1 to open the rengay. Once marked complete, it moves to "Join a Rengay" for a second poet to discover and respond.</p>
      </div>
      <input
        className="title-input"
        placeholder="Title of this rengay — you can change it later if you like"
        value={title}
        onChange={e => { setTitle(e.target.value); if (titleError) setTitleError(false); }}
      />
      {titleError && <p style={{ color: "#e05c5c", fontSize: "0.82rem", margin: "-8px 0 8px", fontFamily: "Georgia, serif", fontStyle: "italic" }}>Rengays must have a title — don't worry, you can always change it later.</p>}
      <input
        className="season-input"
        placeholder="Unifying theme (e.g. autumn light, memory) — you can change it later if you like"
        value={theme}
        onChange={e => setTheme(e.target.value)}
      />
      <input className="title-input" placeholder="Your name (Poet A)…" value={authorA} readOnly />
      <div className="rengay-verse-block verse-a">
        <div className="verse-meta">
          <span className="verse-label">Verse 1 — {authorA || "Poet A"}</span>
          <span className="verse-hint">3 lines · haiku</span>
        </div>
        <textarea
          className="poem-textarea rengay-textarea"
          placeholder={"line 1\nline 2\nline 3"}
          autoCapitalize="none"
          value={verse1}
          onChange={e => { setVerse1(enforceLineCount(e.target.value, 3)); setVerse1Complete(false); }}
          rows={3}
        />
        <div className="line-count-hint">{verse1.split("\n").length} / 3 lines</div>
        {!verse1Complete && verse1.trim() && linesReady && (
          <button className="verse-complete-btn" onClick={() => setVerse1Complete(true)}>✓ Verse 1 Complete</button>
        )}
        {verse1Complete && (
          <div className="verse-complete-indicator">✓ Verse 1 marked complete — <span className="edit-link" onClick={() => setVerse1Complete(false)}>edit</span></div>
        )}
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        {verse1Complete && (
          <button className="save-btn" onClick={handleSubmit}>Post to "Join a Rengay"</button>
        )}
        <button className="cancel-btn" onClick={onCancel}>← Back</button>
      </div>
    </div>
  );
}

// ── Join a Rengay ──────────────────────────────────────────────
function JoinRengayCard({ rengay, onJoin, onOpenSuggestions, onWithdraw, username }) {
  const [expanded, setExpanded] = useState(false);
  const [authorB, setAuthorB] = useState(username || "");
useEffect(() => { if (username) setAuthorB(username); }, [username]);
  const [verse2, setVerse2] = useState("");
  const [verse2Complete, setVerse2Complete] = useState(false);

  const handleJoin = () => {
    if (!verse2.trim() || !verse2Complete || !authorB.trim()) return;
    const verses = [...rengay.verses];
    verses[1] = verse2;
    onJoin({
      ...rengay,
      status: "inprogress",
      authorB: authorB.trim(),
      verses,
      verseComplete: [true, true, false, false, false, false],
    });
  };

  const verse2Lines = verse2.split("\n");
  const canMarkComplete = verse2Lines.length >= 2 && verse2Lines[0].trim().length > 0 && verse2Lines[1].trim().length > 0;
  const canJoin = verse2Complete && authorB.trim().length > 0;

  return (
    <div className={`poem-card ${expanded ? "expanded" : ""}`}>
      <div className="poem-card-top" onClick={() => setExpanded(!expanded)}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div>
            <span className="mini-badge rengay-badge">Awaiting Poet B</span>
            <span className="poem-card-title">{rengay.title}</span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <span className="poem-date">Started {formatDate(rengay.ts)}</span>
            {rengay.authorA && <span className="poets-tag">by {rengay.authorA}</span>}
            {rengay.theme && <span className="poets-tag">· {rengay.theme}</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }} onClick={e => e.stopPropagation()}>
          <button className="suggestions-btn" onClick={() => onOpenSuggestions(rengay.id)}>💬 Suggestions</button>
          {username === rengay.authorA && (
  <button className="withdraw-btn" onClick={() => { if (window.confirm("Withdraw this rengay from Join a Rengay? It will be permanently deleted.")) onWithdraw(rengay.id); }}>Withdraw</button>
)}
          <span style={{ color: "var(--muted)" }}>{expanded ? "▲" : "▼"}</span>
        </div>
      </div>
      {expanded && (
        <div className="poem-card-body">
          <div className="rv-block rv-a" style={{ marginBottom: 16 }}>
            <span className="rv-label">Verse 1 — {rengay.authorA || "Poet A"} <span style={{ opacity: 0.5 }}>· 3 lines · haiku</span></span>
            <pre className="poem-text">{rengay.verses[0]}</pre>
          </div>

          {username === rengay.authorA ? (
            <p style={{ fontSize: "0.82rem", color: "var(--muted)", lineHeight: 1.6 }}>
              Your rengay is open — waiting for a second poet to join and write Verse 2.
            </p>
          ) : (
            <>
              <p style={{ fontSize: "0.82rem", color: "var(--muted)", marginBottom: 16, lineHeight: 1.6 }}>
                Enter your name, write Verse 2, and mark it complete. Then click "Join this Rengay as Poet B" to close the poem to just the two of you.
              </p>

              <input
                className="title-input"
                placeholder="Your name (Poet B)…"
                value={authorB}
                readOnly
                style={{ marginBottom: 10 }}
              />

              <div className="rengay-verse-block verse-b">
                <div className="verse-meta">
                  <span className="verse-label">Verse 2 — {authorB || "Poet B"}</span>
                  <span className="verse-hint">2 lines · couplet</span>
                </div>
                <textarea
                  className="poem-textarea rengay-textarea"
                  placeholder={"line 1\nline 2"}
                  autoCapitalize="none"
                  value={verse2}
                  onChange={e => { setVerse2(enforceLineCount(e.target.value, 2)); setVerse2Complete(false); }}
                  rows={2}
                />
                <div className="line-count-hint">{verse2.split("\n").length} / 2 lines</div>
                {!verse2Complete && canMarkComplete && (
                  <button className="verse-complete-btn" onClick={() => setVerse2Complete(true)}>✓ Verse 2 Complete</button>
                )}
                {verse2Complete && (
                  <div className="verse-complete-indicator">✓ Verse 2 marked complete — <span className="edit-link" onClick={() => setVerse2Complete(false)}>edit</span></div>
                )}
              </div>

              {verse2Complete && !authorB.trim() && (
                <p style={{ fontSize: "0.8rem", color: "var(--accent-warm)", marginTop: 8 }}>Please enter your name above to join.</p>
              )}

              {canJoin && (
                <button
                  className="save-btn"
                  style={{ background: "linear-gradient(135deg, var(--rengay-color) 0%, #8a5ea8 100%)", color: "#fff" }}
                  onClick={handleJoin}
                >
                  Join this Rengay as Poet B
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── In Progress Rengay Card ────────────────────────────────────
function InProgressRengayCard({ rengay, onUpdate, onComplete, onOpenSuggestions, onViewClean, onAbandon, onStepOut, onVerseComplete, username, userId, messages, onSendMessage }) {
const [verses, setVerses] = useState(rengay.verses);
  const [verseComplete, setVerseComplete] = useState(rengay.verseComplete || RENGAY_STRUCTURE.map(() => false));
  const [title, setTitle] = useState(rengay.title);
  const [theme, setTheme] = useState(rengay.theme || "");
  const [editingVerse, setEditingVerse] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [stepInRole, setStepInRole] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingTheme, setEditingTheme] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [showChat, setShowChat] = useState(false);

  const myMessages = (messages || []).filter(m => m.rengayId === rengay.id);
  const isPoet = username === rengay.authorA || username === rengay.authorB;

  const handleSendMessage = () => {
    if (!chatInput.trim()) return;
    onSendMessage(rengay.id, chatInput.trim());
    setChatInput("");
  };

  const filledCount = verseComplete.filter(Boolean).length;
  const allComplete = filledCount === 6;
  const nextOpenIndex = verseComplete.findIndex(c => !c);

  const abandonRequestedBy = rengay.abandonRequestedBy || null;

  const openRole = rengay.seekingPoet
    ? (!rengay.authorA ? "A" : !rengay.authorB ? "B" : null)
    : null;

  const handleSaveTitle = () => { setEditingTitle(false); onUpdate({ ...rengay, title, theme, verses, verseComplete }); };
  const handleSaveTheme = () => { setEditingTheme(false); onUpdate({ ...rengay, title, theme, verses, verseComplete }); };

  const myRole = username === rengay.authorA ? "A" : username === rengay.authorB ? "B" : null;
  const handleVerseChange = (i, val) => {
    const updated = [...verses];
    updated[i] = enforceLineCount(val, RENGAY_STRUCTURE[i].lines);
    setVerses(updated);
  };

  const markVerseComplete = (i) => {
    const vc = [...verseComplete]; vc[i] = true; setVerseComplete(vc);
    setEditingVerse(null);
    onUpdate({ ...rengay, title, theme, verses, verseComplete: vc });
    if (onVerseComplete) onVerseComplete(rengay, i);
  };

  const getPoetName = (author) => author === "A" ? (rengay.authorA || "Poet A") : (rengay.authorB || "Poet B");

  // Step in — clears seeking tag immediately, sets new poet name
  const handleStepIn = () => {
    if (!stepInRole || !username) return;
    onUpdate({
      ...rengay,
      authorA: stepInRole === "A" ? username : rengay.authorA,
      authorB: stepInRole === "B" ? username : rengay.authorB,
      authorAId: stepInRole === "A" ? userId : rengay.authorAId,
      authorBId: stepInRole === "B" ? userId : rengay.authorBId,
      seekingPoet: false,
      abandonRequestedBy: null,
    });
    setStepInRole(null);
  };

  const handleStepOut = (poetRole) => {
    onStepOut(rengay.id, poetRole);
  };

  const handleRequestAbandon = (poetRole) => {
    onUpdate({ ...rengay, title, theme, verses, verseComplete, abandonRequestedBy: poetRole });
  };

  // Cancel abandon request
  const handleCancelAbandon = () => {
    onUpdate({ ...rengay, title, theme, verses, verseComplete, abandonRequestedBy: null });
  };

  // Confirm abandon — only available to the OTHER poet
  const handleConfirmAbandon = () => {
    onAbandon(rengay.id);
  };

  return (
    <div className={`poem-card ${expanded ? "expanded" : ""}`}>
      <div className="poem-card-top" onClick={() => setExpanded(!expanded)}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div>
            <span className="mini-badge rengay-badge">In Progress</span>
            {rengay.seekingPoet && <span className="mini-badge seeking-badge">Seeking New Poet</span>}
            {abandonRequestedBy && <span className="mini-badge abandon-requested-badge">Abandon Requested</span>}
            <span className="poem-card-title">{title}</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span className="poem-date">Started {formatDate(rengay.ts)}</span>
            <span className="progress-indicator">{filledCount}/6 verses</span>
            {rengay.authorA && rengay.authorB && <span className="poets-tag">{rengay.authorA} & {rengay.authorB}</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }} onClick={e => e.stopPropagation()}>
          <button className="view-clean-btn" onClick={() => onViewClean(rengay)}>View Rengay</button>
          <button className="suggestions-btn" onClick={() => onOpenSuggestions(rengay.id)}>💬 Suggestions</button>
          <span style={{ color: "var(--muted)" }}>{expanded ? "▲" : "▼"}</span>
        </div>
      </div>

      {expanded && (
        <div className="poem-card-body">

          {/* Step In notice — shown when a poet spot is open */}
          {rengay.seekingPoet && openRole && (
            <div className="step-in-notice">
              <p className="step-in-title">This rengay is seeking a new Poet {openRole}</p>
              <p style={{ fontSize: "0.82rem", color: "var(--muted)", marginBottom: 12, lineHeight: 1.6 }}>
                The previous Poet {openRole} has stepped out. If you'd like to continue this poem, click below to step in. You'll take over the Poet {openRole} role and pick up from the next verse.
              </p>
              {!stepInRole && (
                <button className="step-in-btn" onClick={() => setStepInRole(openRole)}>
                  Step In as Poet {openRole}
                </button>
              )}
              {stepInRole && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <p style={{ fontSize: "0.85rem", color: "var(--text)" }}>Stepping in as: <strong>{username}</strong></p>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button className="verse-complete-btn" onClick={handleStepIn}>
                      ✓ Confirm — I am the new Poet {stepInRole}
                    </button>
                    <button className="danger-btn" onClick={() => setStepInRole(null)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Solo abandon notice — shown when partner's account was deleted */}
          {rengay.seekingPoet && isPoet && myRole !== null && openRole !== myRole && (
            <div className="abandon-notice">
              <p className="abandon-notice-text">
                Your rengay partner has left this poem. You can wait for a new poet to step in, or abandon the poem if you'd like to remove it.
              </p>
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button className="danger-btn abandon-btn" onClick={handleConfirmAbandon}>
                  Abandon — Delete this Rengay
                </button>
              </div>
            </div>
          )}

          {/* Abandon request notice — shown to the OTHER poet to confirm */}
          {abandonRequestedBy && isPoet && myRole !== abandonRequestedBy && (
            <div className="abandon-notice">
              <p className="abandon-notice-text">
                <strong>{getPoetName(abandonRequestedBy)}</strong> has requested to abandon this rengay.
                {" "}Confirm below to permanently delete it.
              </p>
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button className="danger-btn abandon-btn" onClick={handleConfirmAbandon}>
                  Confirm — Delete this Rengay
                </button>
                <button className="danger-btn" onClick={handleCancelAbandon}>
                  Decline
                </button>
              </div>
            </div>
          )}

          {/* Abandon request notice — shown to the poet who made the request */}
          {abandonRequestedBy && isPoet && myRole === abandonRequestedBy && (
            <div className="abandon-notice">
              <p className="abandon-notice-text">
                You've requested to abandon this rengay. Waiting for your partner to confirm.
              </p>
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button className="danger-btn" onClick={handleCancelAbandon}>
                  Cancel Request
                </button>
              </div>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: "0.82rem", color: "var(--muted)" }}>
              {rengay.theme && <span>Theme: <span style={{ color: "var(--text)" }}>{theme}</span></span>}
            </div>
          </div>

          <div className="verse-progress-bar">
            {RENGAY_STRUCTURE.map((v, i) => (
              <div key={i} className={`verse-pip ${verseComplete[i] ? "filled" : ""} ${v.author === "A" ? "pip-a" : "pip-b"}`} title={`Verse ${i + 1}`} />
            ))}
          </div>

          {RENGAY_STRUCTURE.map((v, i) => {
            const isComplete = verseComplete[i];
            const isNext = i === nextOpenIndex;
            const isEditing = editingVerse === i;
            const isLocked = !isComplete && !isNext;
            return (
              <div key={i} className={`rengay-verse-block ${v.author === "A" ? "verse-a" : "verse-b"} ${isLocked ? "verse-locked" : ""}`}>
                <div className="verse-meta">
                  <span className="verse-label">{v.label} — {getPoetName(v.author)}</span>
                  <span className="verse-hint">{v.hint}</span>
                </div>
                {isLocked && <div className="verse-locked-msg">Waiting for Verse {i} to be completed first.</div>}
                {isComplete && !isEditing && (
                  <>
                    <pre className="poem-text" style={{ marginTop: 8 }}>{verses[i]}</pre>
                    {myRole === v.author && (
                      <div style={{ marginTop: 8 }}>
                        <button className="edit-verse-btn" onClick={() => setEditingVerse(i)}>Edit Your Verse</button>
                      </div>
                    )}
                  </>
                )}
                {((isNext || isEditing) && myRole === v.author) && (
                  <>
                    <textarea
                      className="poem-textarea rengay-textarea"
                      placeholder={v.lines === 3 ? "line 1\nline 2\nline 3" : "line 1\nline 2"}
                      autoCapitalize="none"
                      value={verses[i]}
                      onChange={e => handleVerseChange(i, e.target.value)}
                      rows={v.lines}
                      autoFocus={isEditing}
                    />
                    <div className="line-count-hint">{verses[i].split("\n").length} / {v.lines} lines</div>
                    {verses[i].trim() && verses[i].split("\n").filter(l => l.trim()).length === v.lines && (
                      <button className="verse-complete-btn" onClick={() => markVerseComplete(i)}>✓ Verse {i + 1} Complete</button>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {allComplete
            ? <div style={{ textAlign: "center" }}>
                <p style={{ fontSize: "0.75rem", color: "var(--muted)", fontStyle: "italic", marginBottom: 8 }}>Note: once the poem moves to the Library, the Poet's Chat and its messages will no longer be available, and the title and theme will no longer be editable.</p>
                <button className="complete-btn" onClick={() => onComplete(rengay.id)}>✓ Mark as Complete — Move to Library</button>
              </div>
            : <p className="complete-hint">{6 - filledCount} verse{6 - filledCount !== 1 ? "s" : ""} remaining.</p>
          }

          {isPoet && (() => {
            const isPoetA = username === rengay.authorA;
            const myNotify = isPoetA ? rengay.notifyA : rengay.notifyB;
            const handleToggleNotify = (val) => onUpdate({
              ...rengay, title, theme, verses, verseComplete,
              notifyA: isPoetA ? val : rengay.notifyA,
              notifyB: isPoetA ? rengay.notifyB : val,
            });
            return (
              <div style={{ marginTop: 12, marginBottom: 4 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={myNotify !== false} onChange={e => handleToggleNotify(e.target.checked)} style={{ accentColor: "var(--accent)", width: 14, height: 14 }} />
                  <span style={{ color: "var(--muted)", fontSize: "0.78rem" }}>Notify me when my partner adds a verse</span>
                </label>
              </div>
            );
          })()}

          {/* Poets' Chat */}
          {isPoet && (
            <div className="poets-chat">
              <button className="danger-zone-toggle" onClick={() => setShowChat(!showChat)}>
                {showChat ? "▲ Hide poets' chat" : `▼ Poets' chat${myMessages.length > 0 ? ` (${myMessages.length})` : ""}`}
              </button>
              {showChat && (
                <div className="chat-inner">
                  <p className="chat-desc">Private conversation between {rengay.authorA || "Poet A"} and {rengay.authorB || "Poet B"} — only visible to you two.</p>
                  <div className="chat-messages">
                    {myMessages.length === 0
                      ? <div className="chat-empty">No messages yet — start the conversation.</div>
                      : myMessages.map(m => (
                        <div key={m.id} className={`chat-msg ${m.authorName === username ? "chat-msg-mine" : "chat-msg-theirs"}`}>
                          <span className="chat-msg-author">{m.authorName}</span>
                          <span className="chat-msg-text">{m.content}</span>
                          <span className="chat-msg-time">{formatDate(m.ts)}</span>
                        </div>
                      ))
                    }
                  </div>
                  <div className="chat-input-row">
                    <textarea
                      className="chat-input"
                      placeholder="Write a message…"
                      value={chatInput}
                      rows={2}
                      onChange={e => setChatInput(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSendMessage(); } }}
                      style={{ resize: "none" }}
                    />
                    <button className="chat-send-btn" onClick={handleSendMessage} disabled={!chatInput.trim()}>Send</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Manage section */}
          {isPoet && <div className="danger-zone">
            <button className="danger-zone-toggle" onClick={() => setShowManage(!showManage)}>
              {showManage ? "▲ Hide options" : "▼ Manage this rengay"}
            </button>
            {showManage && (
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>

                {/* Edit Title & Theme */}
                <div>
                  <p className="danger-zone-label">Edit title & theme</p>
                  {!editingTitle && !editingTheme && (
                    <div style={{ display: "flex", gap: 10 }}>
                      <button className="danger-btn" onClick={() => setEditingTitle(true)}>Edit Title</button>
                      <button className="danger-btn" onClick={() => setEditingTheme(true)}>Edit Theme</button>
                    </div>
                  )}
                  {editingTitle && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                      <label className="field-label">Title</label>
                      <input className="inline-edit-input" value={title} onChange={e => setTitle(e.target.value)} autoFocus />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="verse-complete-btn" onClick={handleSaveTitle}>Save Title</button>
                        <button className="danger-btn" onClick={() => setEditingTitle(false)}>Cancel</button>
                      </div>
                    </div>
                  )}
                  {editingTheme && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                      <label className="field-label">Theme</label>
                      <input className="inline-edit-input" value={theme} onChange={e => setTheme(e.target.value)} placeholder="optional…" autoFocus />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="verse-complete-btn" onClick={handleSaveTheme}>Save Theme</button>
                        <button className="danger-btn" onClick={() => setEditingTheme(false)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Step Out */}
                <div>
                  <p className="danger-zone-label">Step out of this rengay</p>
                  <p style={{ fontSize: "0.8rem", color: "var(--muted)", marginBottom: 10, lineHeight: 1.6 }}>
                    If you need to leave this poem, you can step out of your own role. The rengay will be tagged as "Seeking New Poet" and remain open until another community member steps in. You can only step out of your own role.
                  </p>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {username === rengay.authorA && (
  <button className="danger-btn" onClick={() => { if (window.confirm(`Step out as ${rengay.authorA || "Poet A"}? The rengay will remain open for a new poet to join.`)) handleStepOut("A"); }}>
    Step out as {rengay.authorA || "Poet A"}
  </button>
)}
{username === rengay.authorB && (
  <button className="danger-btn" onClick={() => { if (window.confirm(`Step out as ${rengay.authorB || "Poet B"}? The rengay will remain open for a new poet to join.`)) handleStepOut("B"); }}>
    Step out as {rengay.authorB || "Poet B"}
  </button>
)}
                  </div>
                </div>

                {/* Abandon — requires both poets */}
                <div style={{ paddingTop: 12, borderTop: "1px solid var(--border)" }}>
                  <p className="danger-zone-label">Abandon this rengay</p>
                  <p style={{ fontSize: "0.8rem", color: "var(--muted)", marginBottom: 10, lineHeight: 1.6 }}>
                    Abandoning permanently deletes this rengay. Both poets must agree — one poet requests abandonment, and the other must confirm before it is deleted.
                  </p>
                  {!abandonRequestedBy && (
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {username === rengay.authorA && (
  <button className="danger-btn abandon-btn" onClick={() => handleRequestAbandon("A")}>
    {rengay.authorA || "Poet A"} requests abandon
  </button>
)}
{username === rengay.authorB && (
  <button className="danger-btn abandon-btn" onClick={() => handleRequestAbandon("B")}>
    {rengay.authorB || "Poet B"} requests abandon
  </button>
)}
                    </div>
                  )}
                  {abandonRequestedBy && (
                    <p style={{ fontSize: "0.8rem", color: "var(--accent-warm)" }}>
                      Waiting for the other poet to confirm above.
                    </p>
                  )}
                </div>

              </div>
            )}
          </div>}

        </div>
      )}
    </div>
  );
}

// ── Completed Rengay Card ──────────────────────────────────────
function RengayLibraryCard({ poem, onViewClean }) {
  const withdrawalRequestedBy = poem.withdrawalRequestedBy || null;
  return (
    <div className="poem-card">
      <div className="poem-card-top" onClick={() => onViewClean(poem)} style={{ cursor: "pointer" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div>
            <span className="mini-badge rengay-badge">Rengay</span>
            {withdrawalRequestedBy && <span className="mini-badge abandon-requested-badge">Withdrawal Requested</span>}
            <span className="poem-card-title">{poem.title}</span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <span className="poem-date">{formatDate(poem.ts)}</span>
            {poem.authorA && poem.authorB && <span className="poets-tag">by {poem.authorA} & {poem.authorB}</span>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }} onClick={e => e.stopPropagation()}>
          <button className="view-clean-btn" onClick={() => onViewClean(poem)}>View Rengay</button>
        </div>
      </div>
    </div>
  );
}

// ── Home Page ──────────────────────────────────────────────────
function HomePage({ onNavigate, counts, isAdmin, adminDmMessages, onOpenAdminChat }) {
  const hasAdminDms = adminDmMessages && adminDmMessages.length > 0;
  const lastDm = hasAdminDms ? adminDmMessages[adminDmMessages.length - 1] : null;
  const hasUnread = lastDm?.sender === "admin" && !lastDm?.read;
  return (
    <div className="home-page">
      <p className="home-welcome">
        A place to write and share rengay — six-verse collaborative poems in the English-language haiku tradition. Begin a poem, wait for another voice, and see where it leads.
      </p>
      <div className="home-actions">
        <button className="home-action-btn primary-action" onClick={() => onNavigate("start")}>
          <span className="home-action-label">Start a Rengay</span>
          <span className="home-action-desc">Open a new poem with Verse 1</span>
        </button>
        <button className="home-action-btn" onClick={() => onNavigate("join")}>
          <span className="home-action-label">Join a Rengay</span>
          <span className="home-action-desc">{counts.awaiting > 0 ? `${counts.awaiting} poem${counts.awaiting !== 1 ? "s" : ""} waiting for a second voice` : "Browse poems waiting for a second voice"}</span>
        </button>
        <button className="home-action-btn" onClick={() => onNavigate("inprogress")}>
          <span className="home-action-label">Rengay in Progress</span>
          <span className="home-action-desc">{counts.inprogress > 0 ? `${counts.inprogress} poem${counts.inprogress !== 1 ? "s" : ""} currently being written` : "Poems currently being written"}</span>
        </button>
        <button className="home-action-btn" onClick={() => onNavigate("library")}>
          <span className="home-action-label">Library</span>
          <span className="home-action-desc">{counts.completed > 0 ? `${counts.completed} completed poem${counts.completed !== 1 ? "s" : ""}` : "Completed poems"}</span>
        </button>
        <button className="home-action-btn" onClick={() => onNavigate("myrengay")}>
          <span className="home-action-label">My Rengay</span>
          <span className="home-action-desc">{counts.myrengay > 0 ? `${counts.myrengay} poem${counts.myrengay !== 1 ? "s" : ""} you're part of` : "Poems you're writing"}</span>
        </button>
        {isAdmin && (
          <button className="home-action-btn" onClick={() => onNavigate("admin")} style={{borderColor: "#C9A84C", background: "rgba(201,168,76,0.06)"}}>
            <span className="home-action-label" style={{color: "#C9A84C"}}>Admin Panel</span>
            <span className="home-action-desc">Manage poems, users, banners & messages</span>
          </button>
        )}
        {!isAdmin && hasAdminDms && (
          <button className="home-action-btn" onClick={onOpenAdminChat} style={{borderColor: hasUnread ? "#c0392b" : "#5a2020", background: hasUnread ? "rgba(192,57,43,0.15)" : "rgba(139,26,26,0.06)"}}>
            <span className="home-action-label" style={{color: hasUnread ? "#e74c3c" : "#a05050"}}>{hasUnread ? "● Message from Ba Poetry" : "Message from Ba Poetry"}</span>
            <span className="home-action-desc" style={{color: hasUnread ? "#c0392b" : "#5a2020"}}>{hasUnread ? "You have a new message — tap to read" : "Tap to view conversation"}</span>
          </button>
        )}
      </div>
    </div>
  );
}

// ── Password Reset Screen ───────────────────────────────────────
function PasswordResetScreen({ onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    const { error } = await supabase.auth.updateUser({ password });
    if (error) { setError(error.message); return; }
    setSuccess(true);
    setTimeout(() => onDone(), 2000);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#111", border: "1px solid #333", borderRadius: 12, padding: 40, width: "100%", maxWidth: 400 }}>
        <h2 style={{ color: "#C9A84C", fontFamily: "serif", textAlign: "center", marginBottom: 24 }}>Set New Password</h2>
        {success ? (
          <p style={{ color: "#C9A84C", textAlign: "center" }}>Password updated! Taking you back to the app…</p>
        ) : (
          <>
            <PasswordInput value={password} onChange={e => setPassword(e.target.value)} placeholder="New password" style={{ padding: "10px 14px", marginBottom: 12, background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, color: "#fff", fontSize: "1rem" }} />
            <PasswordInput value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirm new password" style={{ padding: "10px 14px", marginBottom: 12, background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, color: "#fff", fontSize: "1rem" }} />
            {error && <p style={{ color: "#e05c5c", fontSize: "0.9rem", marginBottom: 12 }}>{error}</p>}
            <button
              onClick={handleSubmit}
              style={{ width: "100%", padding: "12px", background: "#C9A84C", color: "#000", border: "none", borderRadius: 8, fontSize: "1rem", fontWeight: 600, cursor: "pointer" }}
            >
              Update Password
            </button>
            <button
              onClick={onDone}
              style={{ width: "100%", padding: "12px", marginTop: 8, background: "transparent", color: "#888", border: "none", fontSize: "0.9rem", cursor: "pointer" }}
            >
              Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Supabase converters ────────────────────────────────────────
function convertPoem(p) {
  return {
    id: p.id,
    ts: new Date(p.created_at).getTime(),
    title: p.title || "Untitled Rengay",
    theme: p.theme || "",
    status: p.status,
    authorA: p.author_a_name || "",
    authorB: p.author_b_name || "",
    authorAId: p.author_a || null,
    authorBId: p.author_b || null,
    verses: p.verses || ["", "", "", "", "", ""],
    verseComplete: p.verse_complete || [false, false, false, false, false, false],
    verseNotes: p.verse_notes || ["", "", "", "", "", ""],
    seekingPoet: p.seeking_poet || false,
    abandonRequestedBy: p.abandon_requested_by || null,
    withdrawalRequestedBy: p.withdrawal_requested_by || null,
    notifyA: p.notify_a !== false,
    notifyB: p.notify_b !== false,
    info: p.info || "",
    type: "rengay",
  };
}

function convertSuggestion(s) {
  return {
    id: s.id,
    poemId: s.rengay_id,
    name: s.author_name || "Anonymous",
    userId: s.author_id || null,
    verseNum: s.verse_num ? String(s.verse_num) : null,
    text: s.content || "",
    type: s.type || "comment",
    ts: new Date(s.created_at).getTime(),
  };
}

// ── Main App ─
export default function App() {
  const validTabs = ["home","start","join","inprogress","myrengay","library","admin"];
  const [tab, setTab] = useState(() => {
    const hash = window.location.hash.replace("#", "");
    return validTabs.includes(hash) ? hash : "home";
  });
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [infoPage, setInfoPage] = useState(null);
  const [cleanView, setCleanView] = useState(null);
  const [isLibraryCleanView, setIsLibraryCleanView] = useState(false);

  const [awaitingRengay, setAwaitingRengay] = useState([]);
  const [inProgressRengay, setInProgressRengay] = useState([]);
  const [completedRengay, setCompletedRengay] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionPoem, setActiveSuggestionPoem] = useState(null);
  const [messages, setMessages] = useState([]);
  const [notifyNewRengay, setNotifyNewRengay] = useState(false);
  const [notifyPartnerVerse, setNotifyPartnerVerse] = useState(true);
  const [notifyPoemComplete, setNotifyPoemComplete] = useState(true);
  const [notifyIdleNudge, setNotifyIdleNudge] = useState(true);
  const [notifyChat, setNotifyChat] = useState(true);
  const [notifOpen, setNotifOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [dataLoading, setDataLoading] = useState(true);
  const [activeBanner, setActiveBanner] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminSection, setAdminSection] = useState("banner");
  const [adminDmMessages, setAdminDmMessages] = useState([]);
  const [showAdminChat, setShowAdminChat] = useState(false);
  const [adminDmInput, setAdminDmInput] = useState("");
  const [adminAllDms, setAdminAllDms] = useState([]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === "PASSWORD_RECOVERY") {
        setShowPasswordReset(true);
      }
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  async function loadAllData() {
    setDataLoading(true);
    const isAdminUser = user.email === "haikutheory@gmail.com";
    const [{ data: poems }, { data: suggs }, { data: msgs }, { data: prefs }, { data: banner }, { data: userList }, { data: dmMsgs }] = await Promise.all([
      supabase.from("rengay").select("*").order("created_at", { ascending: false }),
      supabase.from("suggestions").select("*").order("created_at", { ascending: false }),
      supabase.from("messages").select("*").order("created_at", { ascending: true }),
      supabase.from("user_preferences").select("notify_new_rengay, notify_partner_verse, notify_poem_complete, notify_idle_nudge, notify_chat").eq("user_id", user.id).maybeSingle(),
      supabase.from("admin_banner").select("*").eq("active", true).order("created_at", { ascending: false }).limit(1).maybeSingle(),
      isAdminUser ? supabase.from("usernames").select("username, user_id") : Promise.resolve({ data: [] }),
      isAdminUser
        ? supabase.from("admin_messages").select("*").order("created_at", { ascending: true })
        : supabase.from("admin_messages").select("*").eq("user_id", user.id).order("created_at", { ascending: true }),
    ]);
    if (banner) { setActiveBanner(banner); setBannerDismissed(false); }
    else setActiveBanner(null);
    if (userList) setAdminUsers(userList);
    if (dmMsgs) {
      if (isAdminUser) setAdminAllDms(dmMsgs);
      else setAdminDmMessages(dmMsgs);
    }
    if (prefs) {
      setNotifyNewRengay(prefs.notify_new_rengay || false);
      setNotifyPartnerVerse(prefs.notify_partner_verse !== false);
      setNotifyPoemComplete(prefs.notify_poem_complete !== false);
      setNotifyIdleNudge(prefs.notify_idle_nudge !== false);
      setNotifyChat(prefs.notify_chat !== false);
    }
    if (poems) {
      setAwaitingRengay(poems.filter(p => p.status === "awaiting").map(convertPoem));
      setInProgressRengay(poems.filter(p => p.status === "inprogress").map(convertPoem));
      setCompletedRengay(poems.filter(p => p.status === "complete").map(convertPoem));
    }
    if (suggs) {
      setSuggestions(suggs.map(convertSuggestion));
    }
    if (msgs) {
      setMessages(msgs.map(m => ({
        id: m.id,
        rengayId: m.rengay_id,
        authorName: m.author_name,
        content: m.content,
        ts: new Date(m.created_at).getTime(),
      })));
    }
    setDataLoading(false);
  }

  useEffect(() => {
    if (!user) return;
    loadAllData();
  }, [user?.id]);

  const navigate = (newTab) => {
    setTab(newTab);
    window.history.pushState(null, "", "#" + newTab);
  };

  useEffect(() => {
    const handlePopState = () => {
      const hash = window.location.hash.replace("#", "");
      setTab(validTabs.includes(hash) ? hash : "home");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (!user) return;
    const silentRefresh = async () => {
      const [{ data: poems }, { data: suggs }, { data: msgs }] = await Promise.all([
        supabase.from("rengay").select("*").order("created_at", { ascending: false }),
        supabase.from("suggestions").select("*").order("created_at", { ascending: false }),
        supabase.from("messages").select("*").order("created_at", { ascending: true }),
      ]);
      if (poems) {
        setAwaitingRengay(poems.filter(p => p.status === "awaiting").map(convertPoem));
        setInProgressRengay(poems.filter(p => p.status === "inprogress").map(convertPoem));
        setCompletedRengay(poems.filter(p => p.status === "complete").map(convertPoem));
      }
      if (suggs) setSuggestions(suggs.map(convertSuggestion));
      if (msgs) setMessages(msgs.map(m => ({ id: m.id, rengayId: m.rengay_id, authorName: m.author_name, content: m.content, ts: new Date(m.created_at).getTime() })));
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") silentRefresh();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const interval = setInterval(silentRefresh, 30000);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(interval);
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("realtime-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "rengay" }, (payload) => {
        const { eventType, new: n, old: o } = payload;
        if (eventType === "INSERT") {
          const poem = convertPoem(n);
          if (n.status === "awaiting") setAwaitingRengay(prev => prev.find(r => r.id === n.id) ? prev : [poem, ...prev]);
          else if (n.status === "inprogress") setInProgressRengay(prev => prev.find(r => r.id === n.id) ? prev : [poem, ...prev]);
          else if (n.status === "complete") setCompletedRengay(prev => prev.find(r => r.id === n.id) ? prev : [poem, ...prev]);
        } else if (eventType === "UPDATE") {
          const poem = convertPoem(n);
          if (o.status === "awaiting" && n.status === "inprogress") {
            setAwaitingRengay(prev => prev.filter(r => r.id !== n.id));
            setInProgressRengay(prev => prev.find(r => r.id === n.id) ? prev.map(r => r.id === n.id ? poem : r) : [poem, ...prev]);
          } else if (o.status === "inprogress" && n.status === "complete") {
            setInProgressRengay(prev => prev.filter(r => r.id !== n.id));
            setCompletedRengay(prev => prev.find(r => r.id === n.id) ? prev.map(r => r.id === n.id ? poem : r) : [poem, ...prev]);
          } else if (n.status === "awaiting") {
            setAwaitingRengay(prev => prev.map(r => r.id === n.id ? poem : r));
          } else if (n.status === "inprogress") {
            setInProgressRengay(prev => prev.map(r => r.id === n.id ? poem : r));
          } else if (n.status === "complete") {
            setCompletedRengay(prev => prev.map(r => r.id === n.id ? poem : r));
          }
        } else if (eventType === "DELETE") {
          setAwaitingRengay(prev => prev.filter(r => r.id !== o.id));
          setInProgressRengay(prev => prev.filter(r => r.id !== o.id));
          setCompletedRengay(prev => prev.filter(r => r.id !== o.id));
        }
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "suggestions" }, (payload) => {
        const s = payload.new;
        setSuggestions(prev => prev.find(x => x.id === s.id) ? prev : [...prev, convertSuggestion(s)]);
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "suggestions" }, (payload) => {
        setSuggestions(prev => prev.filter(x => x.id !== payload.old.id));
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, (payload) => {
        const m = payload.new;
        setMessages(prev => prev.find(x => x.id === m.id) ? prev : [...prev, {
          id: m.id, rengayId: m.rengay_id, authorName: m.author_name,
          content: m.content, ts: new Date(m.created_at).getTime(),
        }]);
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "admin_messages" }, (payload) => {
        const m = payload.new;
        if (user?.email === "haikutheory@gmail.com") {
          // Admin sees new user replies in real-time
          if (m.sender === "user") setAdminAllDms(prev => prev.find(x => x.id === m.id) ? prev : [...prev, m]);
        } else {
          // User sees new admin messages in real-time
          if (m.sender === "admin") setAdminDmMessages(prev => prev.find(x => x.id === m.id) ? prev : [...prev, m]);
        }
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "admin_messages" }, () => {
        if (user?.email !== "haikutheory@gmail.com") setAdminDmMessages([]);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user?.id]);

  if (authLoading) return null;
  if (showPasswordReset) return <PasswordResetScreen onDone={() => setShowPasswordReset(false)} />;
  if (!user && infoPage !== "copyright") return <AuthScreen onLogin={(u) => setUser(u)} onShowTerms={() => setInfoPage("copyright")} />;
  if (dataLoading) return null;

  const isAdmin = user?.email === "haikutheory@gmail.com";

  const adminAction = async (payload) => {
    const { data, error } = await supabase.functions.invoke("admin-action", { body: payload });
    if (error) throw error;
    return data;
  };

  const adminDeletePoem = async (id) => {
    const { error } = await supabase.from("rengay").delete().eq("id", id);
    if (!error) {
      setAwaitingRengay(prev => prev.filter(r => r.id !== id));
      setInProgressRengay(prev => prev.filter(r => r.id !== id));
      setCompletedRengay(prev => prev.filter(r => r.id !== id));
    }
  };

  const adminDeleteSuggestion = async (id) => {
    const { error } = await supabase.from("suggestions").delete().eq("id", id);
    if (!error) setSuggestions(prev => prev.filter(s => s.id !== id));
  };

  const adminDeleteMessage = async (id) => {
    const { error } = await supabase.from("messages").delete().eq("id", id);
    if (!error) setMessages(prev => prev.filter(m => m.id !== id));
  };

  const adminSaveBanner = async (message, dismissible) => {
    await supabase.from("admin_banner").update({ active: false }).eq("active", true);
    const { data } = await supabase.from("admin_banner").insert({ message, dismissible, active: true }).select().single();
    if (data) { setActiveBanner(data); setBannerDismissed(false); }
  };

  const adminDeactivateBanner = async () => {
    await supabase.from("admin_banner").update({ active: false }).eq("active", true);
    setActiveBanner(null);
  };

  const adminUpdateVerse = async (poem, verseIndex, newText) => {
    const updated = { ...poem, verses: poem.verses.map((v, i) => i === verseIndex ? newText : v) };
    await supabase.from("rengay").update({ verses: updated.verses }).eq("id", poem.id);
    if (poem.status === "inprogress") setInProgressRengay(prev => prev.map(r => r.id === poem.id ? updated : r));
    if (poem.status === "complete") setCompletedRengay(prev => prev.map(r => r.id === poem.id ? updated : r));
  };

  const sendAdminDmReply = async (content) => {
    if (!content.trim()) return;
    const { data } = await supabase.from("admin_messages").insert({ user_id: user.id, sender: "user", content: content.trim(), read: false }).select().single();
    if (data) setAdminDmMessages(prev => [...prev, data]);
  };

  const adminSendDm = async (userId, content) => {
    if (!content.trim()) return;
    const { data } = await supabase.from("admin_messages").insert({ user_id: userId, sender: "admin", content: content.trim(), read: false }).select().single();
    if (data) setAdminAllDms(prev => [...prev, data]);
  };

  const adminDeleteDmThread = async (userId) => {
    await supabase.from("admin_messages").delete().eq("user_id", userId);
    setAdminAllDms(prev => prev.filter(m => m.user_id !== userId));
  };

  const adminMarkThreadRead = async (userId) => {
    await supabase.from("admin_messages").update({ read: true }).eq("user_id", userId).eq("sender", "user").eq("read", false);
    setAdminAllDms(prev => prev.map(m => m.user_id === userId && m.sender === "user" ? { ...m, read: true } : m));
  };

  const markDmsRead = async () => {
    const unread = adminDmMessages.filter(m => m.sender === "admin" && !m.read);
    if (unread.length === 0) return;
    await supabase.from("admin_messages").update({ read: true }).eq("user_id", user.id).eq("sender", "admin").eq("read", false);
    setAdminDmMessages(prev => prev.map(m => m.sender === "admin" ? { ...m, read: true } : m));
  };

  const togglePref = async (dbKey, val, setter) => {
    setter(val);
    await supabase.from("user_preferences").upsert({ user_id: user.id, [dbKey]: val }, { onConflict: "user_id" });
  };

  const handleDeleteAccount = async () => {
    if (!deleteReason.trim()) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const { error } = await supabase.functions.invoke("delete-account", { body: { reason: deleteReason.trim() } });
      if (error) throw error;
      await supabase.auth.signOut();
      setUser(null);
    } catch (e) {
      setDeleteError("Something went wrong. Please try again.");
      setDeleteLoading(false);
    }
  };

  const sendNotification = async (payload) => {
    try {
      await supabase.functions.invoke("send-notification", { body: payload });
    } catch (e) {
      console.error("Notification error:", e);
    }
  };

  const handleVerseComplete = async (rengay, verseIndex) => {
    const poetWhoWrote = RENGAY_STRUCTURE[verseIndex].author;
    const recipientUserId = poetWhoWrote === "A" ? rengay.authorBId : rengay.authorAId;
    const recipientName   = poetWhoWrote === "A" ? rengay.authorB   : rengay.authorA;
    const senderName      = poetWhoWrote === "A" ? rengay.authorA   : rengay.authorB;
    const recipientWantsNotify = poetWhoWrote === "A" ? rengay.notifyB : rengay.notifyA;
    if (!recipientUserId || !recipientWantsNotify) return;
    await sendNotification({
      type: "partner_alert",
      recipientUserId,
      recipientName,
      senderName,
      poemTitle: rengay.title,
      verseNum: verseIndex + 1,
      poemId: rengay.id,
    });
  };

  const startRengay = async (r) => {
    const { data, error } = await supabase.from("rengay").insert({
      title: r.title,
      theme: r.theme || null,
      status: "awaiting",
      author_a: user.id,
      author_a_name: r.authorA,
      verses: r.verses,
      verse_complete: r.verseComplete,
      seeking_poet: false,
      notify_a: notifyPartnerVerse,
    }).select().single();
    if (error) { console.error("startRengay error:", error); return; }
    if (data) {
      setAwaitingRengay(prev => [convertPoem(data), ...prev]);
      navigate("join");
      sendNotification({ type: "new_rengay_blast", senderUserId: user.id, senderName: r.authorA, poemTitle: r.title });
    }
  };

  const joinRengay = async (r) => {
    const { data: rows, error } = await supabase.from("rengay").update({
      status: "inprogress",
      author_b: user.id,
      author_b_name: r.authorB,
      verses: r.verses,
      verse_complete: r.verseComplete,
      notify_b: notifyPartnerVerse,
    }).eq("id", r.id).select();
    if (error) {
      console.error("joinRengay error:", error, "poem id:", r.id);
      alert("Something went wrong joining this rengay. Please try again or contact the site administrator.\n\nError: " + error.message + "\nPoem ID: " + r.id);
      return;
    }
    if (!rows || rows.length === 0) {
      console.error("joinRengay: 0 rows updated for poem id:", r.id);
      alert("Could not join this rengay — it may have already been joined by someone else. Please refresh the page and try again.\n\nPoem ID: " + r.id);
      return;
    }
    const data = rows[0];
    setAwaitingRengay(prev => prev.filter(x => x.id !== r.id));
    setInProgressRengay(prev => [convertPoem(data), ...prev]);
    navigate("inprogress");
    // Notify Poet A that someone joined their rengay
    sendNotification({
      type: "poet_joined",
      recipientUserId: r.authorAId,
      recipientName: r.authorA,
      senderName: r.authorB,
      poemTitle: r.title,
    });
  };

  const withdrawRengay = async (id) => {
    const { error } = await supabase.from("rengay").delete().eq("id", id);
    if (!error) setAwaitingRengay(prev => prev.filter(r => r.id !== id));
  };

  const updateInProgress = async (updated) => {
    setInProgressRengay(prev => prev.map(r => r.id === updated.id ? updated : r));
    await supabase.from("rengay").update({
      title: updated.title,
      theme: updated.theme || null,
      verses: updated.verses,
      verse_complete: updated.verseComplete,
      seeking_poet: updated.seekingPoet || false,
      abandon_requested_by: updated.abandonRequestedBy || null,
      withdrawal_requested_by: updated.withdrawalRequestedBy || null,
      author_a_name: updated.authorA || null,
      author_b_name: updated.authorB || null,
      author_a: updated.authorAId || null,
      author_b: updated.authorBId || null,
      notify_a: updated.notifyA !== false,
      notify_b: updated.notifyB !== false,
    }).eq("id", updated.id);
  };

  const abandonRengay = async (id) => {
    const { error } = await supabase.from("rengay").delete().eq("id", id);
    if (!error) setInProgressRengay(prev => prev.filter(r => r.id !== id));
  };

  const stepOut = async (id, poetRole) => {
    const update = { seeking_poet: true, abandon_requested_by: null };
    if (poetRole === "A") { update.author_a = null; update.author_a_name = null; }
    else { update.author_b = null; update.author_b_name = null; }
    const { error } = await supabase.from("rengay").update(update).eq("id", id);
    if (!error) {
      setInProgressRengay(prev => prev.map(r => {
        if (r.id !== id) return r;
        return {
          ...r,
          authorA: poetRole === "A" ? "" : r.authorA,
          authorB: poetRole === "B" ? "" : r.authorB,
          authorAId: poetRole === "A" ? null : r.authorAId,
          authorBId: poetRole === "B" ? null : r.authorBId,
          seekingPoet: true,
          abandonRequestedBy: null,
        };
      }));
    }
  };

  const completeRengay = async (id) => {
    const r = inProgressRengay.find(x => x.id === id);
    if (!r) return;
    const { error } = await supabase.from("rengay").update({ status: "complete" }).eq("id", id);
    if (!error) {
      setCompletedRengay(prev => [{ ...r, status: "complete" }, ...prev]);
      setInProgressRengay(prev => prev.filter(x => x.id !== id));
      navigate("library");
      if (r.authorAId) sendNotification({ type: "poem_complete", recipientUserId: r.authorAId, recipientName: r.authorA, senderName: r.authorB, poemTitle: r.title });
      if (r.authorBId) sendNotification({ type: "poem_complete", recipientUserId: r.authorBId, recipientName: r.authorB, senderName: r.authorA, poemTitle: r.title });
    }
  };

  const deleteRengay = async (id) => {
    const { error } = await supabase.from("rengay").delete().eq("id", id);
    if (!error) setCompletedRengay(prev => prev.filter(r => r.id !== id));
  };

  const requestWithdrawal = async (id, poetRole) => {
    const { error } = await supabase.from("rengay").update({ withdrawal_requested_by: poetRole }).eq("id", id);
    if (!error) setCompletedRengay(prev => prev.map(r => r.id === id ? { ...r, withdrawalRequestedBy: poetRole } : r));
  };

  const confirmWithdrawal = async (id) => {
    const { error } = await supabase.from("rengay").delete().eq("id", id);
    if (!error) setCompletedRengay(prev => prev.filter(r => r.id !== id));
  };

  const cancelWithdrawal = async (id) => {
    const { error } = await supabase.from("rengay").update({ withdrawal_requested_by: null }).eq("id", id);
    if (!error) setCompletedRengay(prev => prev.map(r => r.id === id ? { ...r, withdrawalRequestedBy: null } : r));
  };

  const saveRengayInfo = async (id, info) => {
    await supabase.from("rengay").update({ info }).eq("id", id);
    setCompletedRengay(prev => prev.map(r => r.id === id ? { ...r, info } : r));
    if (cleanView?.id === id) setCleanView(prev => ({ ...prev, info }));
  };

  const addSuggestion = async ({ poemId, name, verseNum, text, type }) => {
    const { data, error } = await supabase.from("suggestions").insert({
      rengay_id: poemId,
      author_id: user.id,
      author_name: name,
      verse_num: verseNum ? parseInt(verseNum) : null,
      content: text,
      type: type,
    }).select().single();
    if (!error && data) {
      setSuggestions(prev => [convertSuggestion(data), ...prev]);
    }
  };

  const deleteSuggestion = async (id) => {
    const { error } = await supabase.from("suggestions").delete().eq("id", id).eq("author_id", user.id);
    if (!error) setSuggestions(prev => prev.filter(s => s.id !== id));
  };

  const sendMessage = async (rengayId, content) => {
    const authorName = user?.user_metadata?.username || "Poet";
    const { data, error } = await supabase.from("messages").insert({
      rengay_id: rengayId,
      author_name: authorName,
      content,
    }).select().single();
    if (!error && data) {
      setMessages(prev => [...prev, {
        id: data.id,
        rengayId: data.rengay_id,
        authorName: data.author_name,
        content: data.content,
        ts: new Date(data.created_at).getTime(),
      }]);
      // Notify co-poet
      const rengay = inProgressRengay.find(r => r.id === rengayId);
      if (rengay) {
        const isPoetA = user?.id === rengay.authorAId;
        const recipientUserId = isPoetA ? rengay.authorBId : rengay.authorAId;
        const recipientName = isPoetA ? rengay.authorB : rengay.authorA;
        if (recipientUserId) {
          await sendNotification({ type: "chat_message", recipientUserId, recipientName, senderName: authorName, poemTitle: rengay.title });
        }
      }
    }
  };

  if (infoPage) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Noto+Serif:ital,wght@0,400;0,600;1,400&family=Noto+Sans:wght@300;400;500&display=swap');
          :root { --bg: #0e0d0b; --surface: #16150f; --surface2: #1d1b13; --border: #2e2b1e; --text: #e8e0cc; --muted: #7a7260; --accent: #c8a96e; --rengay-color: #a47eb8; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { background: var(--bg); color: var(--text); font-family: ${FONTS.body}; min-height: 100vh; }
          .info-overlay { min-height: 100vh; background: var(--bg); }
          .info-panel { max-width: 680px; margin: 0 auto; padding: 32px 24px 80px; }
          .info-header { margin-bottom: 32px; }
          .back-nav-btn { background: transparent; border: 1px solid var(--border); border-radius: 8px; color: var(--muted); font-size: 0.85rem; padding: 8px 16px; cursor: pointer; font-family: ${FONTS.body}; transition: all 0.2s; }
          .back-nav-btn:hover { border-color: var(--accent); color: var(--accent); }
          .info-title { font-family: ${FONTS.display}; font-size: 1.9rem; font-weight: 600; color: var(--accent); margin-bottom: 28px; line-height: 1.3; }
          .info-body { display: flex; flex-direction: column; gap: 16px; }
          .info-body p { font-size: 0.95rem; line-height: 1.8; color: var(--text); }
          .info-body h2 { font-family: ${FONTS.display}; font-size: 1.1rem; font-weight: 600; color: var(--text); margin-top: 12px; padding-top: 16px; border-top: 1px solid var(--border); }
          .info-body ol, .info-body ul { padding-left: 1.4rem; display: flex; flex-direction: column; gap: 8px; }
          .info-body li { font-size: 0.92rem; line-height: 1.7; color: var(--text); }
          .info-body strong { color: var(--accent); font-weight: 500; }
          .info-body a { color: var(--accent); text-decoration: underline; text-underline-offset: 3px; }
          .info-body a:hover { opacity: 0.8; }
        `}</style>
        <InfoPage page={infoPage} onClose={() => setInfoPage(null)} />
      </>
    );
  }

  if (cleanView) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Noto+Serif:ital,wght@0,400;0,600;1,400&family=Noto+Sans:wght@300;400;500&display=swap');
          :root { --bg: #0e0d0b; --border: #2e2b1e; --text: #e8e0cc; --muted: #7a7260; --accent: #c8a96e; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { background: var(--bg); color: var(--text); font-family: ${FONTS.body}; min-height: 100vh; }
          .clean-view { max-width: 480px; margin: 0 auto; padding: 60px 24px 80px; }
          .clean-title { font-family: ${FONTS.display}; font-size: 1.8rem; font-weight: 600; color: var(--accent); text-align: center; margin-bottom: 8px; }
          .clean-divider { width: 40px; height: 1px; background: var(--border); margin: 20px auto; }
          .clean-verses { display: flex; flex-direction: column; gap: 28px; }
          .clean-verse { font-family: ${FONTS.display}; font-size: 1.05rem; line-height: 1.65; white-space: pre-wrap; color: var(--text); }
          .back-nav-btn { background: transparent; border: 1px solid var(--border); border-radius: 8px; color: var(--muted); font-size: 0.85rem; padding: 8px 16px; cursor: pointer; font-family: ${FONTS.body}; transition: all 0.2s; }
          .back-nav-btn:hover { border-color: var(--accent); color: var(--accent); }
        `}</style>
        <CleanReadingView
          rengay={cleanView}
          onBack={() => { setCleanView(null); setIsLibraryCleanView(false); }}
          isLibrary={isLibraryCleanView}
          username={user?.user_metadata?.username}
          onOpenSuggestions={setActiveSuggestionPoem}
          onRequestWithdrawal={requestWithdrawal}
          onConfirmWithdrawal={confirmWithdrawal}
          onCancelWithdrawal={cancelWithdrawal}
          onSaveInfo={saveRengayInfo}
        />
        {activeSuggestionPoem && (
          <SuggestionsPanel
            poemId={activeSuggestionPoem}
            suggestions={suggestions}
            onAddSuggestion={addSuggestion}
            onDeleteSuggestion={deleteSuggestion}
            onClose={() => setActiveSuggestionPoem(null)}
            commentsOnly={true}
            username={user?.user_metadata?.username}
            currentUserId={user?.id}
          />
        )}
      </>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Serif:ital,wght@0,400;0,600;1,400&family=Noto+Sans:wght@300;400;500&display=swap');
        :root {
          --bg: #0e0d0b; --surface: #16150f; --surface2: #1d1b13; --border: #2e2b1e;
          --text: #e8e0cc; --muted: #7a7260; --accent: #c8a96e; --accent-warm: #e07b4a;
          --rengay-color: #a47eb8; --haiku-color: #7eb8a4; --green: #7ab87e;
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: var(--bg); color: var(--text); font-family: ${FONTS.body}; min-height: 100vh; }
        .app { max-width: 700px; margin: 0 auto; padding: 32px 20px 80px; }

        .app-header { text-align: center; margin-bottom: 36px; }
        .app-title { font-family: ${FONTS.display}; font-size: 2.4rem; font-weight: 600; color: var(--accent); letter-spacing: 0.04em; margin-bottom: 4px; cursor: pointer; }
        .app-title:hover { opacity: 0.85; }
        .app-subtitle { font-size: 0.82rem; color: var(--muted); letter-spacing: 0.12em; text-transform: uppercase; }
        .divider { width: 60px; height: 1px; background: var(--border); margin: 12px auto; }
        .header-links { display: flex; justify-content: center; gap: 10px; margin-top: 12px; flex-wrap: wrap; }
        .header-link { font-size: 0.72rem; color: var(--rengay-color); text-decoration: none; cursor: pointer; background: none; border: 1px solid rgba(201,168,76,0.4); border-radius: 20px; font-family: ${FONTS.body}; padding: 4px 12px; letter-spacing: 0.03em; transition: background 0.2s, border-color 0.2s, color 0.2s; }
        .header-link:hover { background: rgba(201,168,76,0.12); border-color: var(--rengay-color); color: #e0c070; }

        .back-home-bar { margin-bottom: 20px; }
        .back-home-btn { background: rgba(201,168,76,0.08); border: 1px solid rgba(201,168,76,0.25); color: var(--accent); font-family: ${FONTS.body}; font-size: 0.75rem; letter-spacing: 0.08em; cursor: pointer; padding: 6px 14px; border-radius: 20px; transition: background 0.2s, border-color 0.2s; }
        .back-home-btn:hover { background: rgba(201,168,76,0.16); border-color: rgba(201,168,76,0.5); }

        /* Home page */
        .home-page { padding: 16px 0 40px; }
        .home-welcome { font-family: ${FONTS.display}; font-size: 1rem; color: var(--muted); line-height: 1.9; text-align: center; max-width: 520px; margin: 0 auto 40px; font-style: italic; }
        .home-actions { display: flex; flex-direction: column; gap: 10px; }
        .home-action-btn { width: 100%; background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 18px 20px; text-align: left; cursor: pointer; transition: border-color 0.2s, background 0.2s; display: flex; flex-direction: column; gap: 4px; }
        .home-action-btn:hover { border-color: var(--accent); background: var(--surface2); }
        .home-action-btn.primary-action { border-color: rgba(164,126,184,0.3); background: rgba(164,126,184,0.05); }
        .home-action-btn.primary-action:hover { border-color: var(--rengay-color); background: rgba(164,126,184,0.1); }
        .home-action-label { font-family: ${FONTS.display}; font-size: 1.05rem; color: var(--text); font-weight: 600; }
        .home-action-desc { font-size: 0.8rem; color: var(--muted); }

        .editor-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 24px; }
        .editor-header { margin-bottom: 20px; }
        .form-badge { display: inline-block; font-size: 0.75rem; padding: 3px 10px; border-radius: 12px; margin-bottom: 8px; letter-spacing: 0.06em; font-family: ${FONTS.display}; }
        .rengay-badge { background: rgba(164,126,184,0.15); color: var(--rengay-color); border: 1px solid rgba(164,126,184,0.3); }
        .form-desc { font-size: 0.8rem; color: var(--muted); line-height: 1.6; }

        .title-input, .season-input, .author-input { width: 100%; background: var(--surface2); border: 1px solid var(--border); border-radius: 6px; padding: 9px 12px; color: var(--text); font-family: ${FONTS.display}; font-size: 0.92rem; margin-bottom: 10px; outline: none; transition: border-color 0.2s; }
        .title-input:focus, .season-input:focus, .author-input:focus { border-color: var(--accent); }

        .inline-edit-input { width: 100%; background: var(--surface2); border: 1px solid var(--border); border-radius: 6px; padding: 7px 10px; color: var(--text); font-family: ${FONTS.display}; font-size: 0.9rem; outline: none; transition: border-color 0.2s; }
        .inline-edit-input:focus { border-color: var(--accent); }
        .field-label { display: block; font-size: 0.7rem; color: var(--muted); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 5px; }

        .poem-textarea { width: 100%; background: var(--bg); border: 1px solid var(--border); border-radius: 6px; padding: 12px; color: var(--text); font-family: ${FONTS.display}; font-size: 1.05rem; line-height: 1.9; resize: none; outline: none; transition: border-color 0.2s; margin-top: 8px; }
        .poem-textarea:focus { border-color: var(--accent); }
        .rengay-textarea { min-height: 56px; }
        .line-count-hint { font-size: 0.7rem; color: var(--muted); margin-top: 4px; font-family: ${FONTS.mono}; }

        .rengay-verse-block { border-radius: 8px; padding: 14px; margin-bottom: 12px; transition: opacity 0.2s; }
        .verse-a { background: rgba(126,184,164,0.05); border: 1px solid rgba(126,184,164,0.12); }
        .verse-b { background: rgba(164,126,184,0.05); border: 1px solid rgba(164,126,184,0.12); }
        .verse-locked { opacity: 0.4; pointer-events: none; }
        .verse-locked-msg { font-size: 0.78rem; color: var(--muted); font-style: italic; margin-top: 8px; }
        .verse-meta { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
        .verse-label { font-size: 0.78rem; font-weight: 500; color: var(--muted); letter-spacing: 0.06em; }
        .verse-hint { font-size: 0.72rem; color: var(--muted); opacity: 0.6; }

        .verse-complete-btn { margin-top: 10px; padding: 6px 14px; background: transparent; border: 1px solid var(--green); color: var(--green); border-radius: 6px; font-size: 0.78rem; cursor: pointer; font-family: ${FONTS.body}; transition: all 0.2s; }
        .verse-complete-btn:hover { background: rgba(122,184,126,0.1); }
        .verse-complete-indicator { margin-top: 8px; font-size: 0.78rem; color: var(--green); }
        .edit-link { color: var(--muted); cursor: pointer; text-decoration: underline; }
        .edit-verse-btn { margin-top: 10px; padding: 6px 14px; background: transparent; border: 1px solid var(--border); color: var(--muted); border-radius: 6px; font-size: 0.78rem; cursor: pointer; font-family: ${FONTS.body}; transition: all 0.2s; }
        .edit-verse-btn:hover { border-color: var(--accent); color: var(--accent); }

        .save-btn { margin-top: 16px; flex: 1; padding: 12px; background: linear-gradient(135deg, var(--accent) 0%, #b8924a 100%); color: #0e0d0b; border: none; border-radius: 8px; font-size: 0.88rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer; font-family: ${FONTS.body}; transition: opacity 0.2s; }
        .save-btn:hover { opacity: 0.88; }
        .cancel-btn { margin-top: 16px; padding: 12px 20px; background: transparent; color: var(--muted); border: 1px solid var(--border); border-radius: 8px; font-size: 0.85rem; cursor: pointer; font-family: ${FONTS.body}; white-space: nowrap; }
        .cancel-btn:hover { border-color: var(--accent); color: var(--accent); }
        .complete-btn { margin-top: 16px; width: 100%; padding: 12px; background: linear-gradient(135deg, var(--green) 0%, #4a9850 100%); color: #0e0d0b; border: none; border-radius: 8px; font-size: 0.88rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer; font-family: ${FONTS.body}; }
        .complete-hint { margin-top: 14px; text-align: center; font-size: 0.8rem; color: var(--muted); font-style: italic; }

        .verse-progress-bar { display: flex; gap: 6px; margin-bottom: 18px; }
        .verse-pip { flex: 1; height: 6px; border-radius: 3px; background: var(--border); transition: background 0.3s; }
        .verse-pip.filled.pip-a { background: var(--haiku-color); }
        .verse-pip.filled.pip-b { background: var(--rengay-color); }

        .progress-indicator { font-size: 0.7rem; color: var(--rengay-color); background: rgba(164,126,184,0.1); border: 1px solid rgba(164,126,184,0.2); padding: 2px 8px; border-radius: 10px; }
        .poets-tag { font-size: 0.7rem; color: var(--muted); }
        .section-heading { font-family: ${FONTS.display}; font-size: 0.78rem; color: var(--muted); letter-spacing: 0.14em; text-transform: uppercase; margin-bottom: 14px; padding-bottom: 8px; border-bottom: 1px solid var(--border); }

        .poem-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 14px 16px; margin-bottom: 10px; transition: border-color 0.2s; }
        .poem-card:hover { border-color: rgba(200,169,110,0.3); }
        .poem-card.expanded { border-color: var(--accent); }
        .poem-card-top { display: flex; justify-content: space-between; align-items: center; cursor: pointer; }
        .mini-badge { font-size: 0.7rem; padding: 2px 7px; border-radius: 10px; margin-right: 8px; font-family: ${FONTS.display}; }
        .poem-card-title { font-family: ${FONTS.display}; font-size: 0.95rem; color: var(--text); }
        .poem-date { font-size: 0.72rem; color: var(--muted); }

        .view-clean-btn { background: rgba(200,169,110,0.1); border: 1px solid rgba(200,169,110,0.3); color: var(--accent); font-size: 0.72rem; padding: 3px 10px; border-radius: 12px; cursor: pointer; font-family: ${FONTS.body}; transition: all 0.2s; }
        .view-clean-btn:hover { background: rgba(200,169,110,0.2); }
        .suggestions-btn { background: rgba(164,126,184,0.12); border: 1px solid rgba(164,126,184,0.3); color: var(--rengay-color); font-size: 0.72rem; padding: 3px 10px; border-radius: 12px; cursor: pointer; font-family: ${FONTS.body}; transition: all 0.2s; }
        .suggestions-btn:hover { background: rgba(164,126,184,0.22); }
        .delete-btn { background: transparent; border: none; color: var(--muted); font-size: 1rem; cursor: pointer; padding: 0 4px; line-height: 1; }
        .delete-btn:hover { color: var(--accent-warm); }

        .poem-card-body { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--border); }
        .kigo-tag { font-size: 0.78rem; color: var(--haiku-color); }
        .poem-text { font-family: ${FONTS.display}; font-size: 1rem; line-height: 2; white-space: pre-wrap; color: var(--text); }
        .rengay-view { display: flex; flex-direction: column; gap: 10px; }
        .rv-block { padding: 10px 12px; border-radius: 6px; }
        .rv-a { background: rgba(126,184,164,0.07); }
        .rv-b { background: rgba(164,126,184,0.07); }
        .rv-label { display: block; font-size: 0.7rem; color: var(--muted); margin-bottom: 4px; letter-spacing: 0.06em; }
        .empty-state { text-align: center; padding: 40px 20px; color: var(--muted); font-family: ${FONTS.display}; font-style: italic; font-size: 0.95rem; line-height: 2; }

        .overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 100; display: flex; justify-content: flex-end; }
        .side-panel { width: 420px; max-width: 100vw; height: 100vh; background: var(--surface); border-left: 1px solid var(--border); display: flex; flex-direction: column; overflow-y: auto; padding: 24px; gap: 20px; }
        .panel-header { display: flex; justify-content: space-between; align-items: center; }
        .panel-title { font-family: ${FONTS.display}; font-size: 1.1rem; color: var(--accent); }
        .close-btn { background: transparent; border: none; color: var(--muted); font-size: 1.4rem; cursor: pointer; line-height: 1; }
        .panel-desc { font-size: 0.8rem; color: var(--muted); line-height: 1.6; }
        .suggestion-form { background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; padding: 16px; display: flex; flex-direction: column; gap: 10px; }
        .sugg-input { width: 100%; background: var(--bg); border: 1px solid var(--border); border-radius: 6px; padding: 8px 12px; color: var(--text); font-family: ${FONTS.display}; font-size: 0.9rem; outline: none; }
        .sugg-type-row { display: flex; gap: 8px; }
        .sugg-type-btn { flex: 1; padding: 7px; border-radius: 6px; border: 1px solid var(--border); background: transparent; color: var(--muted); font-size: 0.78rem; cursor: pointer; font-family: ${FONTS.body}; transition: all 0.2s; }
        .sugg-type-btn.active { border-color: var(--rengay-color); color: var(--rengay-color); background: rgba(164,126,184,0.1); }
        .sugg-textarea { width: 100%; background: var(--bg); border: 1px solid var(--border); border-radius: 6px; padding: 10px 12px; color: var(--text); font-family: ${FONTS.display}; font-size: 0.95rem; line-height: 1.8; resize: vertical; outline: none; }
        .sugg-submit-btn { padding: 9px; background: linear-gradient(135deg, var(--rengay-color) 0%, #8a5ea8 100%); color: #fff; border: none; border-radius: 6px; font-size: 0.82rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer; font-family: ${FONTS.body}; }
        .sugg-list { display: flex; flex-direction: column; gap: 12px; }
        .sugg-empty { text-align: center; color: var(--muted); font-style: italic; font-size: 0.85rem; padding: 20px 0; font-family: ${FONTS.display}; }
        .sugg-item { border-radius: 8px; padding: 12px 14px; border: 1px solid var(--border); }
        .sugg-verse { background: rgba(164,126,184,0.05); }
        .sugg-comment { background: rgba(200,169,110,0.05); }
        .sugg-item-header { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; flex-wrap: wrap; }
        .sugg-author { font-size: 0.78rem; font-weight: 500; color: var(--text); }
        .sugg-verse-tag { font-size: 0.7rem; padding: 2px 8px; border-radius: 10px; background: rgba(164,126,184,0.15); color: var(--rengay-color); }
        .sugg-comment-tag { font-size: 0.7rem; padding: 2px 8px; border-radius: 10px; background: rgba(200,169,110,0.15); color: var(--accent); }
        .sugg-date { font-size: 0.68rem; color: var(--muted); margin-left: auto; }
        .sugg-text { font-family: ${FONTS.display}; font-size: 0.9rem; line-height: 1.8; white-space: pre-wrap; color: var(--text); }

        .verse-note-display { margin-top: 8px; font-size: 0.8rem; color: var(--muted); font-style: italic; background: var(--surface2); border-left: 2px solid var(--border); padding: 6px 10px; border-radius: 4px; line-height: 1.5; }
        .step-in-notice { background: rgba(122,184,126,0.06); border: 1px solid rgba(122,184,126,0.2); border-radius: 8px; padding: 14px; margin-bottom: 16px; }
        .step-in-title { font-family: ${FONTS.display}; font-size: 0.9rem; color: var(--green); margin-bottom: 8px; font-weight: 600; }
        .step-in-btn { padding: 8px 18px; background: transparent; border: 1px solid var(--green); color: var(--green); border-radius: 6px; font-size: 0.82rem; cursor: pointer; font-family: ${FONTS.body}; transition: all 0.2s; }
        .step-in-btn:hover { background: rgba(122,184,126,0.1); }
        .abandon-requested-badge { background: rgba(224,74,74,0.15); color: #e04a4a; border: 1px solid rgba(224,74,74,0.3); margin-right: 8px; }
        .withdraw-btn { background: transparent; border: 1px solid rgba(224,123,74,0.3); color: var(--accent-warm); font-size: 0.72rem; padding: 3px 10px; border-radius: 12px; cursor: pointer; font-family: ${FONTS.body}; transition: all 0.2s; }
        .withdraw-btn:hover { background: rgba(224,123,74,0.1); }
        .abandon-notice { background: rgba(224,74,74,0.06); border: 1px solid rgba(224,74,74,0.2); border-radius: 8px; padding: 14px; margin-bottom: 16px; }
        .abandon-notice-text { font-size: 0.85rem; color: var(--text); line-height: 1.6; }
        .danger-zone { margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--border); }
        .danger-zone-toggle { background: transparent; border: none; color: var(--muted); font-size: 0.75rem; cursor: pointer; font-family: ${FONTS.body}; padding: 0; letter-spacing: 0.06em; }
        .danger-zone-toggle:hover { color: var(--text); }
        .danger-zone-label { font-size: 0.7rem; color: var(--muted); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 10px; }
        .danger-btn { padding: 7px 14px; background: transparent; border: 1px solid var(--border); color: var(--muted); border-radius: 6px; font-size: 0.78rem; cursor: pointer; font-family: ${FONTS.body}; transition: all 0.2s; }
        .danger-btn:hover { border-color: var(--accent); color: var(--accent); }
        .abandon-btn:hover { border-color: #e04a4a; color: #e04a4a; }
        .footer-link { font-size: 0.75rem; color: var(--muted); cursor: pointer; background: none; border: none; font-family: ${FONTS.body}; text-decoration: underline; padding: 0; transition: color 0.2s; }
        .footer-link:hover { color: var(--accent); }
        .poets-chat { margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--border); }
        .chat-inner { margin-top: 12px; display: flex; flex-direction: column; gap: 10px; }
        .chat-desc { font-size: 0.78rem; color: var(--muted); line-height: 1.5; }
        .chat-messages { display: flex; flex-direction: column; gap: 8px; max-height: 260px; overflow-y: auto; background: var(--bg); border: 1px solid var(--border); border-radius: 8px; padding: 12px; }
        .chat-empty { font-size: 0.82rem; color: var(--muted); font-style: italic; text-align: center; padding: 12px 0; }
        .chat-msg { display: flex; flex-direction: column; gap: 2px; padding: 8px 10px; border-radius: 8px; max-width: 85%; }
        .chat-msg-mine { background: rgba(164,126,184,0.12); border: 1px solid rgba(164,126,184,0.2); align-self: flex-end; }
        .chat-msg-theirs { background: var(--surface2); border: 1px solid var(--border); align-self: flex-start; }
        .chat-msg-author { font-size: 0.7rem; font-weight: 600; color: var(--accent); letter-spacing: 0.04em; }
        .chat-msg-text { font-size: 0.88rem; color: var(--text); line-height: 1.5; white-space: pre-wrap; }
        .chat-msg-time { font-size: 0.65rem; color: var(--muted); margin-top: 2px; }
        .chat-input-row { display: flex; gap: 8px; align-items: flex-end; }
        .chat-input { flex: 1; background: var(--surface2); border: 1px solid var(--border); border-radius: 6px; padding: 8px 12px; color: var(--text); font-family: ${FONTS.body}; font-size: 0.88rem; outline: none; transition: border-color 0.2s; line-height: 1.5; }
        .chat-input:focus { border-color: var(--rengay-color); }
        .chat-send-btn { padding: 8px 16px; background: linear-gradient(135deg, var(--rengay-color) 0%, #8a5ea8 100%); color: #fff; border: none; border-radius: 6px; font-size: 0.82rem; font-weight: 600; cursor: pointer; font-family: ${FONTS.body}; transition: opacity 0.2s; white-space: nowrap; }
        .chat-send-btn:disabled { opacity: 0.4; cursor: default; }
        .chat-send-btn:not(:disabled):hover { opacity: 0.85; }
      `}</style>

      <div className="app">
        <div className="app-header">
          <div className="app-title" onClick={() => navigate("home")}>場 Ba</div>
          <div className="divider" />
          <div className="app-subtitle">A place for rengay</div>
          <div className="header-links">
            <button className="header-link" onClick={() => setInfoPage("what")}>What is Rengay?</button>
            <button className="header-link" onClick={() => setInfoPage("how")}>How to Write Rengay</button>
            <button className="header-link" onClick={() => setInfoPage("howto")}>How to Use This App</button>
            
          </div>
        </div>

        {activeBanner && !bannerDismissed && (
          <div style={{background: "#2a2200", border: "1px solid #5a4a00", borderRadius: 8, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "flex-start", gap: 10}}>
            <span style={{color: "#C9A84C", fontSize: "0.85rem", lineHeight: 1.6, flex: 1}}>{activeBanner.message}</span>
            {activeBanner.dismissible && (
              <button onClick={() => setBannerDismissed(true)} style={{background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: "1rem", lineHeight: 1, flexShrink: 0}}>×</button>
            )}
          </div>
        )}

        {tab !== "home" && (
          <div className="back-home-bar">
            <button className="back-home-btn" onClick={() => navigate("home")}>← Home</button>
          </div>
        )}

        {tab === "home" && (
          <HomePage
            onNavigate={navigate}
            counts={{ awaiting: awaitingRengay.length, inprogress: inProgressRengay.length, completed: completedRengay.length, myrengay: [...inProgressRengay, ...completedRengay].filter(p => p.authorAId === user?.id || p.authorBId === user?.id).length }}
            isAdmin={isAdmin}
            adminDmMessages={adminDmMessages}
            onOpenAdminChat={() => { setShowAdminChat(true); markDmsRead(); }}
          />
        )}

        {tab === "start" && (
          <StartRengayForm onStart={startRengay} onCancel={() => navigate("home")} username={user?.user_metadata?.username} />
        )}

        {tab === "join" && (
  <>
    <div className="section-heading">Rengay awaiting a second poet</div>
    {awaitingRengay.length === 0
      ? <div className="empty-state">no rengay waiting—<br />be the first<br />to open a verse</div>
      : awaitingRengay.map(r => <JoinRengayCard key={r.id} rengay={r} onJoin={joinRengay} onOpenSuggestions={setActiveSuggestionPoem} onWithdraw={withdrawRengay} username={user?.user_metadata?.username} />)
    }
  </>
)}

        {tab === "inprogress" && (
          <>
            <div className="section-heading">Rengay in progress</div>
            {inProgressRengay.length === 0
              ? <div className="empty-state">no rengay in progress—<br />two voices, not yet<br />called to the page</div>
              : inProgressRengay.map(r => (
                <InProgressRengayCard key={r.id} rengay={r} onUpdate={updateInProgress} onComplete={completeRengay} onOpenSuggestions={setActiveSuggestionPoem} onViewClean={setCleanView} onAbandon={abandonRengay} onStepOut={stepOut} onVerseComplete={handleVerseComplete} username={user?.user_metadata?.username} userId={user?.id} messages={messages} onSendMessage={sendMessage} />
              ))
            }
          </>
        )}
        {tab === "myrengay" && (
  <>
    <div className="section-heading">My Rengay</div>
{(() => {
  const username = user?.user_metadata?.username;
  const myAwaiting = awaitingRengay.filter(r => r.authorA === username);
  const myInProgress = inProgressRengay.filter(r => r.authorA === username || r.authorB === username);
  const myCompleted = completedRengay.filter(r => r.authorA === username || r.authorB === username);
  const total = myAwaiting.length + myInProgress.length + myCompleted.length;
  return (
    <div style={{marginBottom: 16, padding: "12px 16px", background: "var(--card-bg)", borderRadius: 8}}>
      <div style={{display: "flex", alignItems: "center", gap: 12, marginBottom: 10}}>
        <div style={{width: 40, height: 40, borderRadius: "50%", background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", color: "#1a1a1a", fontWeight: 600, fontSize: "1.1rem"}}>{username?.[0]?.toUpperCase()}</div>
        <div>
          <div style={{color: "var(--text)", fontSize: "0.95rem"}}>{username}</div>
          <div style={{color: "var(--muted)", fontSize: "0.75rem"}}>{total} poem{total !== 1 ? "s" : ""} · {myCompleted.length} completed</div>
        </div>
      </div>
      <div style={{marginTop: 14, borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 10}}>
        <button onClick={() => setNotifOpen(o => !o)} style={{display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", background: "none", border: "none", cursor: "pointer", padding: 0}}>
          <span style={{color: "var(--muted)", fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em"}}>Email Notification Preferences</span>
          <span style={{color: "var(--muted)", fontSize: "0.7rem"}}>{notifOpen ? "▲" : "▼"}</span>
        </button>
        {notifOpen && (
          <div style={{marginTop: 10}}>
            {[
              { dbKey: "notify_partner_verse", val: notifyPartnerVerse, setter: setNotifyPartnerVerse, label: "Partner adds a verse",        tip: "Sent when your co-poet writes a verse in one of your shared poems. This sets your default — you can also toggle it per-poem." },
              { dbKey: "notify_chat",          val: notifyChat,          setter: setNotifyChat,          label: "New message in Poet's Chat", tip: "Sent when your co-poet leaves a message in the Poet's Chat area of a shared poem." },
              { dbKey: "notify_poem_complete", val: notifyPoemComplete, setter: setNotifyPoemComplete, label: "Poem is completed",            tip: "Sent to both poets when the 6th and final verse is submitted, and the poem moves to the Library." },
              { dbKey: "notify_new_rengay",    val: notifyNewRengay,    setter: setNotifyNewRengay,    label: "New rengay posted",           tip: "Sent when any poet posts a new rengay in Join a Rengay looking for a second voice." },
              { dbKey: "notify_idle_nudge",    val: notifyIdleNudge,    setter: setNotifyIdleNudge,    label: "Poem idle for 7 days",        tip: "A gentle reminder when a poem you're part of hasn't had a new verse in 7 days. Repeats every 3 days while idle." },
            ].map(({ dbKey, val, setter, label, tip }) => (
              <label key={dbKey} style={{display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 8}}>
                <input type="checkbox" checked={val} onChange={e => togglePref(dbKey, e.target.checked, setter)} style={{accentColor: "var(--accent)", width: 14, height: 14, flexShrink: 0}} />
                <span style={{color: "var(--muted)", fontSize: "0.78rem"}}>{label}</span>
                <InfoTip text={tip} />
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
})()}
    {(() => {
      const username = user?.user_metadata?.username;
      const myPoems = [
        ...awaitingRengay.filter(r => r.authorA === username),
        ...inProgressRengay.filter(r => r.authorA === username || r.authorB === username),
        ...completedRengay.filter(r => r.authorA === username || r.authorB === username),
      ];
      return (
  <>
    {myPoems.length === 0 && <div className="empty-state">no rengay yet—<br />begin a verse<br />or join another poet</div>}
    {myPoems.filter(r => r.status === "awaiting").length > 0 && <div className="profile-section-label">Awaiting a second poet</div>}
    {myPoems.filter(r => r.status === "awaiting").map(r => <JoinRengayCard key={r.id} rengay={r} onJoin={joinRengay} onOpenSuggestions={setActiveSuggestionPoem} onWithdraw={withdrawRengay} username={username} />)}
    {myPoems.filter(r => r.status === "inprogress").length > 0 && <div className="profile-section-label">In progress</div>}
    {myPoems.filter(r => r.status === "inprogress").map(r => <InProgressRengayCard key={r.id} rengay={r} onUpdate={updateInProgress} onComplete={completeRengay} onOpenSuggestions={setActiveSuggestionPoem} onViewClean={setCleanView} onAbandon={abandonRengay} onStepOut={stepOut} onVerseComplete={handleVerseComplete} username={username} userId={user?.id} messages={messages} onSendMessage={sendMessage} />)}
    {myPoems.filter(r => r.status === "complete").length > 0 && <div className="profile-section-label">Completed</div>}
    {myPoems.filter(r => r.status === "complete").map(r => <RengayLibraryCard key={r.id} poem={r} onViewClean={(poem) => { setCleanView(poem); setIsLibraryCleanView(true); }} />)}
    <div style={{marginTop: 32, paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column", alignItems: "center", gap: 6}}>
      <button
        onClick={() => { setShowDeleteModal(true); setDeleteReason(""); setDeleteError(null); }}
        style={{background: "transparent", border: "1px solid #5a2020", borderRadius: 6, color: "#a05050", fontSize: "0.68rem", padding: "5px 14px", cursor: "pointer", letterSpacing: "0.03em"}}
      >Delete My Account</button>
      <span style={{color: "var(--muted)", fontSize: "0.63rem", opacity: 0.45}}>Permanently removes your account and poems</span>
    </div>
  </>
);
    })()}
  </>
)}
        {tab === "library" && (
          <>
            <div className="section-heading">{completedRengay.length} completed rengay</div>
            {completedRengay.length === 0
              ? <div className="empty-state">no completed rengay yet—<br />the conversation<br />still unfolding</div>
              : completedRengay.map(r => (
                <RengayLibraryCard
                  key={r.id}
                  poem={r}
                  onViewClean={(poem) => { setCleanView(poem); setIsLibraryCleanView(true); }}
                />
              ))
            }
          </>
        )}

        {tab === "admin" && isAdmin && (
          <AdminPanel
            allPoems={[...awaitingRengay, ...inProgressRengay, ...completedRengay]}
            allSuggestions={suggestions}
            allMessages={messages}
            allUsers={adminUsers}
            activeBanner={activeBanner}
            adminSection={adminSection}
            setAdminSection={setAdminSection}
            onDeletePoem={adminDeletePoem}
            onDeleteSuggestion={adminDeleteSuggestion}
            onDeleteMessage={adminDeleteMessage}
            onSaveBanner={adminSaveBanner}
            onDeactivateBanner={adminDeactivateBanner}
            onUpdateVerse={adminUpdateVerse}
            onAdminAction={adminAction}
            onUsersChanged={() => supabase.from("usernames").select("username, user_id").then(({ data }) => { if (data) setAdminUsers(data); })}
            allDms={adminAllDms}
            onAdminSendDm={adminSendDm}
            onAdminDeleteThread={adminDeleteDmThread}
            onAdminMarkThreadRead={adminMarkThreadRead}
          />
        )}

        <div className="page-footer">
          <button className="footer-link" onClick={() => setInfoPage("copyright")}>Terms of Service</button>
          <span className="footer-sep"> · </span>
          <button className="footer-link" onClick={() => setShowPasswordReset(true)}>Change Password</button>
          <span className="footer-sep"> · </span>
          <button className="footer-link" onClick={() => supabase.auth.signOut()}>Sign Out</button>
          {!isAdmin && (
            <>
              <span className="footer-sep"> · </span>
              <button className="footer-link" onClick={() => { setShowAdminChat(true); markDmsRead(); }}>Contact Site Administrator</button>
            </>
          )}
        </div>
      </div>

      {showAdminChat && !isAdmin && (
        <div style={{position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20}}>
          <div style={{background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 24, maxWidth: 420, width: "100%", maxHeight: "80vh", display: "flex", flexDirection: "column"}}>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16}}>
              <span style={{color: "#C9A84C", fontFamily: "serif", fontSize: "1rem"}}>Contact Site Administrator</span>
              <button onClick={() => setShowAdminChat(false)} style={{background: "none", border: "none", color: "var(--muted)", fontSize: "1.4rem", cursor: "pointer", lineHeight: 1}}>×</button>
            </div>
            <div style={{flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, marginBottom: 14}}>
              {adminDmMessages.map(m => (
                <div key={m.id} style={{display: "flex", flexDirection: "column", alignItems: m.sender === "admin" ? "flex-start" : "flex-end"}}>
                  <div style={{background: m.sender === "admin" ? "rgba(201,168,76,0.1)" : "rgba(255,255,255,0.05)", border: `1px solid ${m.sender === "admin" ? "rgba(201,168,76,0.2)" : "rgba(255,255,255,0.08)"}`, borderRadius: 8, padding: "8px 12px", maxWidth: "85%"}}>
                    <div style={{color: "var(--muted)", fontSize: "0.68rem", marginBottom: 4}}>{m.sender === "admin" ? "Ba Poetry" : "You"} · {formatDate(new Date(m.created_at).getTime())}</div>
                    <div style={{color: "var(--text)", fontSize: "0.85rem", lineHeight: 1.5}}>{m.content}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{display: "flex", gap: 8}}>
              <input
                value={adminDmInput}
                onChange={e => setAdminDmInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendAdminDmReply(adminDmInput); setAdminDmInput(""); } }}
                placeholder="Write a message…"
                style={{flex: 1, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px 10px", color: "var(--text)", fontSize: "0.85rem", fontFamily: "inherit"}}
              />
              <button onClick={() => { sendAdminDmReply(adminDmInput); setAdminDmInput(""); }} style={{background: "var(--accent)", border: "none", borderRadius: 6, color: "#1a1a1a", fontWeight: 600, fontSize: "0.82rem", padding: "8px 16px", cursor: "pointer"}}>Send</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div style={{position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20}}>
          <div style={{background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 28, maxWidth: 380, width: "100%"}}>
            {!deleteConfirmStep ? (
              <>
                <div style={{color: "#e05555", fontSize: "1rem", fontWeight: 600, marginBottom: 8}}>Delete Account</div>
                <p style={{color: "var(--muted)", fontSize: "0.82rem", lineHeight: 1.6, marginBottom: 16}}>
                  This will permanently delete your account, all your awaiting and in-progress poems, and your preferences. Completed poems will remain in the Library.
                </p>
                <p style={{color: "var(--muted)", fontSize: "0.82rem", marginBottom: 8}}>Please tell us why you're leaving:</p>
                <textarea
                  value={deleteReason}
                  onChange={e => setDeleteReason(e.target.value)}
                  placeholder="Your reason (required)…"
                  rows={3}
                  style={{width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px 10px", color: "var(--text)", fontSize: "0.85rem", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box"}}
                />
                <div style={{display: "flex", gap: 10, marginTop: 16}}>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    style={{flex: 1, background: "var(--accent)", border: "none", borderRadius: 7, color: "#1a1a1a", padding: "9px 0", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer"}}
                  >Return to My Rengay</button>
                  <button
                    onClick={() => { setDeleteConfirmText(""); setDeleteConfirmStep(true); }}
                    disabled={!deleteReason.trim()}
                    style={{flex: 1, background: "transparent", border: "1px solid #5a2020", borderRadius: 7, color: "#a05050", padding: "9px 0", fontSize: "0.82rem", cursor: deleteReason.trim() ? "pointer" : "not-allowed", opacity: !deleteReason.trim() ? 0.5 : 1}}
                  >Continue</button>
                </div>
              </>
            ) : (
              <>
                <div style={{color: "#e05555", fontSize: "1rem", fontWeight: 600, marginBottom: 8}}>Are you sure?</div>
                <p style={{color: "var(--muted)", fontSize: "0.82rem", lineHeight: 1.6, marginBottom: 16}}>
                  This cannot be undone. Type <strong style={{color: "var(--text)"}}>yes</strong> below to permanently delete your account.
                </p>
                <input
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder="Type yes to confirm…"
                  style={{width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px 10px", color: "var(--text)", fontSize: "0.85rem", fontFamily: "inherit", boxSizing: "border-box"}}
                />
                {deleteError && <div style={{color: "#e05555", fontSize: "0.78rem", marginTop: 6}}>{deleteError}</div>}
                <div style={{display: "flex", gap: 10, marginTop: 16}}>
                  <button
                    onClick={() => { setDeleteConfirmStep(false); setShowDeleteModal(false); }}
                    style={{flex: 1, background: "var(--accent)", border: "none", borderRadius: 7, color: "#1a1a1a", padding: "9px 0", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer"}}
                  >Return to My Rengay</button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText.trim().toLowerCase() !== "yes" || deleteLoading}
                    style={{flex: 1, background: "transparent", border: "1px solid #5a2020", borderRadius: 7, color: "#a05050", padding: "9px 0", fontSize: "0.82rem", cursor: deleteConfirmText.trim().toLowerCase() === "yes" ? "pointer" : "not-allowed", opacity: (deleteConfirmText.trim().toLowerCase() !== "yes" || deleteLoading) ? 0.5 : 1}}
                  >{deleteLoading ? "Deleting…" : "Permanently Delete"}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeSuggestionPoem && (
        <SuggestionsPanel
          poemId={activeSuggestionPoem}
          suggestions={suggestions}
          onAddSuggestion={addSuggestion}
          onDeleteSuggestion={deleteSuggestion}
          onClose={() => setActiveSuggestionPoem(null)}
          commentsOnly={tab === "library"}
          username={user?.user_metadata?.username}
          currentUserId={user?.id}
        />
      )}
    </>
  );
}
// ── Admin Panel ────────────────────────────────────────────────
function AdminPanel({ allPoems, allSuggestions, allMessages, allUsers, activeBanner, adminSection, setAdminSection, onDeletePoem, onDeleteSuggestion, onDeleteMessage, onSaveBanner, onDeactivateBanner, onUpdateVerse, onAdminAction, onUsersChanged, allDms, onAdminSendDm, onAdminDeleteThread, onAdminMarkThreadRead }) {
  const [bannerMsg, setBannerMsg] = useState(activeBanner?.message || "");
  const [bannerDismissible, setBannerDismissible] = useState(true);
  const [bannerSaving, setBannerSaving] = useState(false);
  const [blastSubject, setBlastSubject] = useState("");
  const [blastMessage, setBlastMessage] = useState("");
  const [blastSending, setBlastSending] = useState(false);
  const [blastResult, setBlastResult] = useState(null);
  const [removeUserId, setRemoveUserId] = useState(null);
  const [removeMode, setRemoveMode] = useState("keep_poems");
  const [removeLoading, setRemoveLoading] = useState(false);
  const [editingVerse, setEditingVerse] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [dmOpenUserId, setDmOpenUserId] = useState(null);
  const [dmInput, setDmInput] = useState("");
  const [newDmUserId, setNewDmUserId] = useState(""); // {label, onConfirm}
  const [confirmText, setConfirmText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);

  const requireConfirm = (label, onConfirm) => {
    setConfirmText("");
    setConfirmAction({ label, onConfirm });
  };

  const handleConfirm = async () => {
    if (!confirmAction || confirmText.trim().toLowerCase() !== "yes") return;
    setConfirmLoading(true);
    await confirmAction.onConfirm();
    setConfirmLoading(false);
    setConfirmAction(null);
    setConfirmText("");
  };

  const sections = ["banner", "blast", "poems", "suggestions", "chats", "users", "dms"];

  const handleSaveBanner = async () => {
    if (!bannerMsg.trim()) return;
    setBannerSaving(true);
    await onSaveBanner(bannerMsg.trim(), bannerDismissible);
    setBannerSaving(false);
  };

  const handleBlast = async () => {
    if (!blastSubject.trim() || !blastMessage.trim()) return;
    setBlastSending(true); setBlastResult(null);
    try {
      const result = await onAdminAction({ action: "email_blast", subject: blastSubject.trim(), message: blastMessage.trim() });
      setBlastResult(`Sent to ${result.sent} users.`);
      setBlastSubject(""); setBlastMessage("");
    } catch { setBlastResult("Error sending — check edge function logs."); }
    setBlastSending(false);
  };

  const handleRemoveUser = (userId, username) => {
    requireConfirm(`Remove user "${username}"`, async () => {
      try {
        await onAdminAction({ action: "remove_user", userId, mode: removeMode });
        await onUsersChanged();
        setRemoveUserId(null);
      } catch { }
    });
  };

  const handleSaveVerse = async () => {
    if (!editingVerse) return;
    const poem = allPoems.find(p => p.id === editingVerse.poemId);
    if (!poem) return;
    await onUpdateVerse(poem, editingVerse.verseIndex, editingVerse.text);
    setEditingVerse(null);
  };

  const sectionBtn = (s, label) => (
    <button key={s} onClick={() => setAdminSection(s)} style={{padding: "6px 14px", borderRadius: 6, border: "1px solid", borderColor: adminSection === s ? "#C9A84C" : "#333", background: adminSection === s ? "rgba(201,168,76,0.12)" : "transparent", color: adminSection === s ? "#C9A84C" : "#888", fontSize: "0.75rem", cursor: "pointer"}}>{label}</button>
  );

  return (
    <div>
      <div className="section-heading">Admin Panel</div>
      <div style={{display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20}}>
        {sectionBtn("banner", "Banner")}
        {sectionBtn("blast", "Email Blast")}
        {sectionBtn("poems", "Poems")}
        {sectionBtn("suggestions", "Suggestions")}
        {sectionBtn("chats", "Chats")}
        {sectionBtn("users", "Users")}
        {sectionBtn("dms", "Direct Messages")}
      </div>

      {/* ── Banner ── */}
      {adminSection === "banner" && (
        <div style={{background: "var(--card-bg)", borderRadius: 8, padding: 16}}>
          <div style={{color: "var(--muted)", fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12}}>In-App Banner</div>
          {activeBanner && (
            <div style={{marginBottom: 16, padding: 12, background: "#2a2200", borderRadius: 6, border: "1px solid #5a4a00"}}>
              <div style={{color: "#C9A84C", fontSize: "0.82rem", marginBottom: 6}}><strong>Active:</strong> {activeBanner.message}</div>
              <div style={{color: "#888", fontSize: "0.75rem", marginBottom: 8}}>{activeBanner.dismissible ? "Dismissible" : "Permanent"}</div>
              <button onClick={() => requireConfirm("Deactivate banner", onDeactivateBanner)} style={{background: "transparent", border: "1px solid #5a2020", borderRadius: 6, color: "#a05050", fontSize: "0.75rem", padding: "4px 12px", cursor: "pointer"}}>Deactivate Banner</button>
            </div>
          )}
          <textarea value={bannerMsg} onChange={e => setBannerMsg(e.target.value)} placeholder="Banner message…" rows={3} style={{width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px 10px", color: "var(--text)", fontSize: "0.85rem", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 10}} />
          <div style={{display: "flex", alignItems: "center", gap: 12, marginBottom: 12}}>
            <label style={{display: "flex", alignItems: "center", gap: 6, cursor: "pointer"}}>
              <input type="radio" checked={bannerDismissible} onChange={() => setBannerDismissible(true)} style={{accentColor: "var(--accent)"}} />
              <span style={{color: "var(--muted)", fontSize: "0.8rem"}}>Dismissible</span>
            </label>
            <label style={{display: "flex", alignItems: "center", gap: 6, cursor: "pointer"}}>
              <input type="radio" checked={!bannerDismissible} onChange={() => setBannerDismissible(false)} style={{accentColor: "var(--accent)"}} />
              <span style={{color: "var(--muted)", fontSize: "0.8rem"}}>Permanent</span>
            </label>
          </div>
          <button onClick={handleSaveBanner} disabled={!bannerMsg.trim() || bannerSaving} style={{background: "var(--accent)", border: "none", borderRadius: 6, color: "#1a1a1a", fontWeight: 600, fontSize: "0.82rem", padding: "8px 20px", cursor: "pointer", opacity: bannerSaving ? 0.6 : 1}}>{bannerSaving ? "Saving…" : "Post Banner"}</button>
        </div>
      )}

      {/* ── Email Blast ── */}
      {adminSection === "blast" && (
        <div style={{background: "var(--card-bg)", borderRadius: 8, padding: 16}}>
          <div style={{color: "var(--muted)", fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12}}>Email Blast — All Users</div>
          <input value={blastSubject} onChange={e => setBlastSubject(e.target.value)} placeholder="Subject line…" style={{width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px 10px", color: "var(--text)", fontSize: "0.85rem", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 10}} />
          <textarea value={blastMessage} onChange={e => setBlastMessage(e.target.value)} placeholder="Message body…" rows={5} style={{width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px 10px", color: "var(--text)", fontSize: "0.85rem", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 10}} />
          {blastResult && <div style={{color: "#C9A84C", fontSize: "0.8rem", marginBottom: 8}}>{blastResult}</div>}
          <button onClick={handleBlast} disabled={!blastSubject.trim() || !blastMessage.trim() || blastSending} style={{background: "var(--accent)", border: "none", borderRadius: 6, color: "#1a1a1a", fontWeight: 600, fontSize: "0.82rem", padding: "8px 20px", cursor: "pointer", opacity: blastSending ? 0.6 : 1}}>{blastSending ? "Sending…" : "Send to All Users"}</button>
        </div>
      )}

      {/* ── Poems ── */}
      {adminSection === "poems" && (
        <div>
          {allPoems.length === 0 && <div className="empty-state">No poems yet.</div>}
          {allPoems.map(poem => (
            <div key={poem.id} style={{background: "var(--card-bg)", borderRadius: 8, padding: 14, marginBottom: 10}}>
              <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8}}>
                <div>
                  <span style={{color: "var(--text)", fontSize: "0.9rem", fontWeight: 600}}>{poem.title}</span>
                  <span style={{color: "var(--muted)", fontSize: "0.75rem", marginLeft: 8}}>{poem.status}</span>
                  <div style={{color: "var(--muted)", fontSize: "0.75rem", marginTop: 2}}>{poem.authorA}{poem.authorB ? ` & ${poem.authorB}` : ""}</div>
                </div>
                <button onClick={() => requireConfirm(`Delete poem "${poem.title}"`, () => onDeletePoem(poem.id))} style={{background: "transparent", border: "1px solid #5a2020", borderRadius: 6, color: "#a05050", fontSize: "0.72rem", padding: "4px 10px", cursor: "pointer"}}>Delete</button>
              </div>
              {poem.verses.map((v, i) => v?.trim() ? (
                <div key={i} style={{marginBottom: 8}}>
                  {editingVerse?.poemId === poem.id && editingVerse?.verseIndex === i ? (
                    <div>
                      <textarea value={editingVerse.text} onChange={e => setEditingVerse({...editingVerse, text: e.target.value})} rows={3} style={{width: "100%", background: "var(--bg)", border: "1px solid var(--accent)", borderRadius: 6, padding: "6px 8px", color: "var(--text)", fontSize: "0.8rem", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box"}} />
                      <div style={{display: "flex", gap: 8, marginTop: 4}}>
                        <button onClick={handleSaveVerse} style={{background: "var(--accent)", border: "none", borderRadius: 5, color: "#1a1a1a", fontSize: "0.72rem", padding: "4px 12px", cursor: "pointer", fontWeight: 600}}>Save</button>
                        <button onClick={() => setEditingVerse(null)} style={{background: "transparent", border: "1px solid #333", borderRadius: 5, color: "#888", fontSize: "0.72rem", padding: "4px 12px", cursor: "pointer"}}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8}}>
                      <pre style={{color: "var(--muted)", fontSize: "0.78rem", margin: 0, whiteSpace: "pre-wrap", flex: 1}}><span style={{color: "#555", fontSize: "0.7rem"}}>V{i+1} </span>{v}</pre>
                      <button onClick={() => setEditingVerse({poemId: poem.id, verseIndex: i, text: v})} style={{background: "transparent", border: "none", color: "#555", fontSize: "0.72rem", cursor: "pointer", padding: "0 4px", flexShrink: 0}}>edit</button>
                    </div>
                  )}
                </div>
              ) : null)}
            </div>
          ))}
        </div>
      )}

      {/* ── Suggestions ── */}
      {adminSection === "suggestions" && (
        <div>
          {allSuggestions.length === 0 && <div className="empty-state">No suggestions yet.</div>}
          {allSuggestions.map(s => (
            <div key={s.id} style={{background: "var(--card-bg)", borderRadius: 8, padding: 12, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10}}>
              <div style={{flex: 1}}>
                <div style={{color: "var(--muted)", fontSize: "0.72rem", marginBottom: 4}}><strong style={{color: "var(--text)"}}>{s.name}</strong> · {s.type}{s.verseNum ? ` · Verse ${s.verseNum}` : ""} · {formatDate(s.ts)}</div>
                <pre style={{color: "var(--text)", fontSize: "0.8rem", margin: 0, whiteSpace: "pre-wrap"}}>{s.text}</pre>
              </div>
              <button onClick={() => requireConfirm(`Delete ${s.type} by ${s.name}`, () => onDeleteSuggestion(s.id))} style={{background: "transparent", border: "1px solid #5a2020", borderRadius: 6, color: "#a05050", fontSize: "0.72rem", padding: "4px 10px", cursor: "pointer", flexShrink: 0}}>Delete</button>
            </div>
          ))}
        </div>
      )}

      {/* ── Chats ── */}
      {adminSection === "chats" && (
        <div>
          {allMessages.length === 0 && <div className="empty-state">No chat messages yet.</div>}
          {allMessages.map(m => (
            <div key={m.id} style={{background: "var(--card-bg)", borderRadius: 8, padding: 12, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10}}>
              <div style={{flex: 1}}>
                <div style={{color: "var(--muted)", fontSize: "0.72rem", marginBottom: 4}}><strong style={{color: "var(--text)"}}>{m.authorName}</strong> · {formatDate(m.ts)}</div>
                <div style={{color: "var(--text)", fontSize: "0.82rem"}}>{m.content}</div>
              </div>
              <button onClick={() => requireConfirm(`Delete message by ${m.authorName}`, () => onDeleteMessage(m.id))} style={{background: "transparent", border: "1px solid #5a2020", borderRadius: 6, color: "#a05050", fontSize: "0.72rem", padding: "4px 10px", cursor: "pointer", flexShrink: 0}}>Delete</button>
            </div>
          ))}
        </div>
      )}

      {/* ── Users ── */}
      {adminSection === "users" && (
        <div>
          {allUsers.length === 0 && <div className="empty-state">No users found.</div>}
          {allUsers.map(u => (
            <div key={u.user_id} style={{background: "var(--card-bg)", borderRadius: 8, padding: 12, marginBottom: 8}}>
              <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: removeUserId === u.user_id ? 10 : 0}}>
                <span style={{color: "var(--text)", fontSize: "0.88rem"}}>{u.username}</span>
                {removeUserId !== u.user_id && (
                  <button onClick={() => setRemoveUserId(u.user_id)} style={{background: "transparent", border: "1px solid #5a2020", borderRadius: 6, color: "#a05050", fontSize: "0.72rem", padding: "4px 10px", cursor: "pointer"}}>Remove</button>
                )}
              </div>
              {removeUserId === u.user_id && (
                <div style={{marginTop: 8}}>
                  <div style={{color: "var(--muted)", fontSize: "0.78rem", marginBottom: 8}}>How should their poems be handled?</div>
                  <div style={{display: "flex", flexDirection: "column", gap: 6, marginBottom: 10}}>
                    <label style={{display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer"}}>
                      <input type="radio" checked={removeMode === "keep_poems"} onChange={() => setRemoveMode("keep_poems")} style={{accentColor: "var(--accent)", marginTop: 2}} />
                      <span style={{color: "var(--muted)", fontSize: "0.78rem"}}>Keep poems — in-progress poems stay open for a new partner, completed poems remain in the Library</span>
                    </label>
                    <label style={{display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer"}}>
                      <input type="radio" checked={removeMode === "delete_all"} onChange={() => setRemoveMode("delete_all")} style={{accentColor: "var(--accent)", marginTop: 2}} />
                      <span style={{color: "var(--muted)", fontSize: "0.78rem"}}>Delete all poems — removes all their poems including completed ones from the Library</span>
                    </label>
                  </div>
                  <div style={{display: "flex", gap: 8}}>
                    <button onClick={() => handleRemoveUser(u.user_id, u.username)} style={{background: "#8b2222", border: "none", borderRadius: 6, color: "#fff", fontSize: "0.78rem", padding: "6px 14px", cursor: "pointer"}}>Confirm Remove</button>
                    <button onClick={() => setRemoveUserId(null)} style={{background: "transparent", border: "1px solid #333", borderRadius: 6, color: "#888", fontSize: "0.78rem", padding: "6px 14px", cursor: "pointer"}}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Direct Messages ── */}
      {adminSection === "dms" && (
        <div>
          <div style={{background: "var(--card-bg)", borderRadius: 8, padding: 14, marginBottom: 12}}>
            <div style={{color: "var(--muted)", fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10}}>Start New Conversation</div>
            <select value={newDmUserId} onChange={e => setNewDmUserId(e.target.value)} style={{width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "7px 10px", color: "var(--text)", fontSize: "0.82rem", marginBottom: 8}}>
              <option value="">Select a user…</option>
              {allUsers.filter(u => u.user_id).map(u => <option key={u.user_id} value={u.user_id}>{u.username}</option>)}
            </select>
            <button onClick={() => { if (newDmUserId) { setDmOpenUserId(newDmUserId); setNewDmUserId(""); } }} disabled={!newDmUserId} style={{background: "var(--accent)", border: "none", borderRadius: 6, color: "#1a1a1a", fontWeight: 600, fontSize: "0.8rem", padding: "7px 16px", cursor: newDmUserId ? "pointer" : "not-allowed", opacity: newDmUserId ? 1 : 0.5}}>Open Conversation</button>
          </div>

          {/* New conversation — no messages yet */}
          {dmOpenUserId && !(allDms || []).some(m => m.user_id === dmOpenUserId) && (() => {
            const u = allUsers.find(x => x.user_id === dmOpenUserId);
            if (!u) return null;
            return (
              <div style={{background: "var(--card-bg)", borderRadius: 8, padding: 14, marginBottom: 10}}>
                <div style={{color: "var(--text)", fontSize: "0.88rem", marginBottom: 12}}><strong>{u.username}</strong> <span style={{color: "var(--muted)", fontSize: "0.72rem"}}>— new conversation</span></div>
                <div style={{display: "flex", gap: 8}}>
                  <input value={dmInput} onChange={e => setDmInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { onAdminSendDm(u.user_id, dmInput); setDmInput(""); } }} placeholder="Write your first message…" style={{flex: 1, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "7px 10px", color: "var(--text)", fontSize: "0.82rem", fontFamily: "inherit"}} />
                  <button onClick={() => { onAdminSendDm(u.user_id, dmInput); setDmInput(""); }} style={{background: "var(--accent)", border: "none", borderRadius: 6, color: "#1a1a1a", fontWeight: 600, fontSize: "0.78rem", padding: "7px 14px", cursor: "pointer"}}>Send</button>
                </div>
              </div>
            );
          })()}

          {/* Active conversations */}
          {(() => {
            const threads = allUsers.map(u => {
              const msgs = (allDms || []).filter(m => m.user_id === u.user_id);
              if (msgs.length === 0) return null;
              return { user: u, msgs };
            }).filter(Boolean);
            if (threads.length === 0) return <div className="empty-state">No active conversations yet.</div>;
            return threads.map(({ user: u, msgs }) => {
              const isOpen = dmOpenUserId === u.user_id;
              const lastMsg = msgs[msgs.length - 1];
              const hasUnreadFromUser = msgs.some(m => m.sender === "user" && !m.read);
              return (
                <div key={u.user_id} style={{background: "var(--card-bg)", borderRadius: 8, padding: 14, marginBottom: 10, border: `1px solid ${hasUnreadFromUser ? "rgba(201,168,76,0.4)" : "transparent"}`}}>
                  <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isOpen ? 12 : 0}}>
                    <button onClick={() => { setDmOpenUserId(isOpen ? null : u.user_id); if (!isOpen && hasUnreadFromUser) onAdminMarkThreadRead(u.user_id); }} style={{background: "none", border: "none", color: "var(--text)", fontSize: "0.88rem", cursor: "pointer", padding: 0, textAlign: "left"}}>
                      <strong>{u.username}</strong>
                      {hasUnreadFromUser && <span style={{color: "#C9A84C", fontSize: "0.72rem", marginLeft: 8}}>● new reply</span>}
                      {!hasUnreadFromUser && <span style={{color: "var(--muted)", fontSize: "0.72rem", marginLeft: 8}}>{msgs.length} message{msgs.length !== 1 ? "s" : ""} · last: {lastMsg.sender === "admin" ? "you" : u.username}</span>}
                    </button>
                    <button onClick={() => requireConfirm(`Delete conversation with ${u.username}`, () => onAdminDeleteThread(u.user_id))} style={{background: "transparent", border: "1px solid #5a2020", borderRadius: 6, color: "#a05050", fontSize: "0.7rem", padding: "3px 10px", cursor: "pointer"}}>Delete Thread</button>
                  </div>
                  {isOpen && (
                    <>
                      <div style={{display: "flex", flexDirection: "column", gap: 8, maxHeight: 280, overflowY: "auto", marginBottom: 10}}>
                        {msgs.map(m => (
                          <div key={m.id} style={{display: "flex", flexDirection: "column", alignItems: m.sender === "admin" ? "flex-end" : "flex-start"}}>
                            <div style={{background: m.sender === "admin" ? "rgba(201,168,76,0.1)" : "rgba(255,255,255,0.05)", border: `1px solid ${m.sender === "admin" ? "rgba(201,168,76,0.2)" : "rgba(255,255,255,0.08)"}`, borderRadius: 8, padding: "7px 11px", maxWidth: "85%"}}>
                              <div style={{color: "var(--muted)", fontSize: "0.66rem", marginBottom: 3}}>{m.sender === "admin" ? "Ba Poetry" : u.username} · {formatDate(new Date(m.created_at).getTime())}</div>
                              <div style={{color: "var(--text)", fontSize: "0.82rem", lineHeight: 1.5}}>{m.content}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{display: "flex", gap: 8}}>
                        <input value={dmInput} onChange={e => setDmInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { onAdminSendDm(u.user_id, dmInput); setDmInput(""); } }} placeholder="Reply as Ba Poetry…" style={{flex: 1, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "7px 10px", color: "var(--text)", fontSize: "0.82rem", fontFamily: "inherit"}} />
                        <button onClick={() => { onAdminSendDm(u.user_id, dmInput); setDmInput(""); }} style={{background: "var(--accent)", border: "none", borderRadius: 6, color: "#1a1a1a", fontWeight: 600, fontSize: "0.78rem", padding: "7px 14px", cursor: "pointer"}}>Send</button>
                      </div>
                    </>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* ── Confirm modal ── */}
      {confirmAction && (
        <div style={{position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20}}>
          <div style={{background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 28, maxWidth: 360, width: "100%"}}>
            <div style={{color: "#e05555", fontSize: "0.95rem", fontWeight: 600, marginBottom: 8}}>Confirm Action</div>
            <p style={{color: "var(--muted)", fontSize: "0.82rem", lineHeight: 1.6, marginBottom: 16}}>{confirmAction.label} — this cannot be undone.</p>
            <p style={{color: "var(--muted)", fontSize: "0.82rem", marginBottom: 8}}>Type <strong style={{color: "var(--text)"}}>yes</strong> to confirm:</p>
            <input
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="Type yes…"
              style={{width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px 10px", color: "var(--text)", fontSize: "0.85rem", fontFamily: "inherit", boxSizing: "border-box"}}
            />
            <div style={{display: "flex", gap: 10, marginTop: 16}}>
              <button onClick={() => setConfirmAction(null)} style={{flex: 1, background: "var(--accent)", border: "none", borderRadius: 7, color: "#1a1a1a", padding: "9px 0", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer"}}>Cancel</button>
              <button
                onClick={handleConfirm}
                disabled={confirmText.trim().toLowerCase() !== "yes" || confirmLoading}
                style={{flex: 1, background: "transparent", border: "1px solid #5a2020", borderRadius: 7, color: "#a05050", padding: "9px 0", fontSize: "0.82rem", cursor: confirmText.trim().toLowerCase() === "yes" ? "pointer" : "not-allowed", opacity: (confirmText.trim().toLowerCase() !== "yes" || confirmLoading) ? 0.5 : 1}}
              >{confirmLoading ? "Working…" : "Confirm"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AuthScreen({ onLogin, onShowTerms }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [username, setUsername] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
const [forgotMode, setForgotMode] = useState(false);
const [forgotSent, setForgotSent] = useState(false);

  async function handleSubmit() {
    setError(null);
    if (isSignUp) {
      if (!username.trim()) { setError("Please choose a username."); return; }
      if (password !== confirmPassword) { setError("Passwords don't match — please try again."); return; }
      const { data: taken } = await supabase.from("usernames").select("username").eq("username", username.trim()).maybeSingle();
      if (taken) { setError("That username is already taken — please choose another."); return; }
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({ email, password, options: { data: { username: username.trim() } } });
      if (signUpError) { setError(signUpError.message); return; }
      if (signUpData?.user) {
        await supabase.from("usernames").insert({ username: username.trim(), user_id: signUpData.user.id });
        await supabase.functions.invoke("send-notification", { body: { type: "new_user_signup", username: username.trim(), email } });
      }
      const { data: { session } } = await supabase.auth.getSession();
      onLogin(session?.user ?? null);
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) { setError(error.message); return; }
      const { data: { session } } = await supabase.auth.getSession();
      onLogin(session?.user ?? null);
    }
  }

  return (
    <div className="auth-screen">
      <h1 className="app-title">場 Ba</h1>
      <p className="app-subtitle">A Place for Rengay</p>
      <div className="auth-box">
        <h2>{isSignUp ? "Create Account" : "Sign In"}</h2>
        {isSignUp && (
  <input
    type="text"
    placeholder="Username"
    value={username}
    onChange={e => setUsername(e.target.value)}
  />
)}
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
        <PasswordInput value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" />
        {isSignUp && (
          <PasswordInput value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm Password" />
        )}
        {isSignUp && <p className="auth-terms">By creating an account you agree to our <button className="auth-terms-link" onClick={onShowTerms}>Terms of Service</button>.</p>}

        {error && <p className="auth-error">{error}</p>}
        <button onClick={handleSubmit}>
          {isSignUp ? "Sign Up" : "Sign In"}
        </button>
        {!isSignUp && !forgotMode && (
  <button className="auth-toggle" onClick={() => { setForgotMode(true); setError(null); }}>
    Forgot password?
  </button>
)}
{forgotMode && (
  <div style={{ marginTop: 12 }}>
    {forgotSent ? (
      <p style={{ color: "#C9A84C", textAlign: "center", fontSize: "0.9rem" }}>Check your inbox for a reset link.</p>
    ) : (
      <>
        <input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          style={{ width: "100%", padding: "10px 14px", marginBottom: 8, background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, color: "#fff", fontSize: "1rem", boxSizing: "border-box" }}
        />
        {error && <p className="auth-error">{error}</p>}
        <button onClick={async () => {
          if (!email) { setError("Please enter your email."); return; }
          const { error } = await supabase.auth.resetPasswordForEmail(email);
          if (error) { setError(error.message); } else { setForgotSent(true); setError(null); }
        }}>Send Reset Email</button>
        <button className="auth-toggle" onClick={() => { setForgotMode(false); setError(null); }}>
          Back to Sign In
        </button>
      </>
    )}
  </div>
)}
        <button className="auth-toggle" onClick={() => setIsSignUp(!isSignUp)}>
          {isSignUp ? "Already have an account? Sign In" : "New here? Create Account"}
        </button>
      </div>
    </div>
  );
}

